import type {
  CogoXyzAttachGithubPrPreviewInput,
  CogoXyzArchiveLibraryItemInput,
  CogoXyzCreateCogoAppInput,
  CogoXyzCreateOpenAiCogoAppInput,
  CogoXyzExportCogoBundleInput,
  CogoXyzGetLibraryItemInput,
  CogoXyzImportFigmaFrameInput,
  CogoXyzImportCogoAssetInput,
  CogoXyzInputType,
  CogoXyzInspectPreviewInput,
  CogoXyzListCloudflareDeploymentsInput,
  CogoXyzListLibraryItemsInput,
  CogoXyzListTemplatesInput,
  CogoXyzPrepareDeployInput,
  CogoXyzPreviewIdeFileInput,
  CogoXyzPublishGithubPrPreviewInput,
  CogoXyzPublishGalleryItemInput,
  CogoXyzRecordCloudflareDeploymentInput,
  CogoXyzRemixTemplateInput,
  CogoXyzShareCogoPreviewInput,
  CogoXyzUpdateCogoAppInput,
} from "@cogo/types-v2";
import type { JsonSchema } from "@cogo/webmcp-runtime";
import {
  approveDesignMutation,
  computeDesignContentHash,
  type DesignMutationKind,
} from "@cogo/design-data/design-mutation";
import {
  assertFigmaCompanionReceipt,
  type FigmaCompanionReceipt,
} from "@cogo/design-data/figma-companion";
import { cogoXyzRegistry } from "@/features/cogo-xyz/cogo-xyz-registry";
import { cogoXyzRegistrySyncCoordinator } from "@/features/cogo-xyz/cogo-xyz-registry-sync-coordinator";
import type { StudioMcpBus } from "./studio-mcp-bus";
import {
  loadCogoXyzRegistryFromProject,
  loadCogoXyzRegistryFromCurrentProject,
  mergeCogoXyzRegistrySnapshots,
  persistGeneratedCogoXyzRegistryToProject,
  saveCogoXyzRegistryToCurrentProject,
  saveCogoXyzRegistryToProject,
} from "@/features/cogo-xyz/cogo-xyz-project-storage";
import {
  importCogoJsonAppInput,
  importPersistedCogoJsonAppInput,
} from "@/features/cogo-xyz/cogo-xyz-import";
import { importSourceAppInput } from "@/features/cogo-xyz/cogo-xyz-source-adapter";
import { generatePromptAppInput } from "@/features/cogo-xyz/cogo-xyz-prompt-generation";
import { runCogoXyzPreviewProof } from "@/features/cogo-xyz/cogo-xyz-preview-proof";
import {
  importFigmaFrameAsCogoApp,
  type FigmaFrameImportInput,
} from "@/features/cogo-xyz/cogo-xyz-figma-import";
import { storeFigmaAccessTokenOnServer } from "@/features/cogo-xyz/figma-token-connection";
import {
  fetchGithubPrContext,
  githubPrContextToMarkdown,
  type CogoXyzGithubPrPreviewInput,
} from "@/features/cogo-xyz/cogo-xyz-github-import";
import {
  listCogoXyzTemplates,
  remixCogoXyzTemplate,
} from "@/features/cogo-xyz/cogo-xyz-template-gallery";
import {
  assertOpenPencilGenerationAdmitted,
  OPENPENCIL_EDITABLE_NODE_TYPES,
  OPENPENCIL_FIG_EDIT_PATCH_SCHEMA,
  OPENPENCIL_VARIABLE_TYPES,
  OpenPencilFigEditPatchSchema,
  normalizeOpenPencilFigmaSourceBundle,
  summarizeOpenPencilFigmaSource,
  type OpenPencilFigEditOperation,
} from "@/features/openpencil-figma/openpencil-figma-source";
import {
  applySuggestedSourceMapping,
  assertOpenPencilSourceReleaseAdmitted,
  bindOpenPencilFigmaCompanionUpstream,
  buildOpenPencilSourceAssemblyManifest,
  carryOpenPencilSourceDecisionsToRevision,
  createOpenPencilSourceWorkspace,
  normalizeOpenPencilSourceWorkspace,
  normalizePersistedOpenPencilSourceWorkspace,
  recordOpenPencilFigmaCompanionMutation,
  recordOpenPencilFigmaCompanionPreparedPatch,
  recordOpenPencilFigmaCompanionSyncBackEvidence,
  recordOpenPencilSourceCandidateBinding,
  recordSourceRevisionVisualDiff,
  recordVerifiedOpenPencilSourceRuntimeProof,
  recordSourceVisualJudgement,
  setAllSourceTokenDecisions,
  setSourcePageDecision,
  setSourceSectionDecision,
  setSourceSurfaceDecision,
  setSourceTokenDecision,
  summarizeOpenPencilSourceWorkspace,
  type OpenPencilSourceWorkspace,
} from "@/features/openpencil-figma/openpencil-source-workspace";
import {
  createFigmaCompanionSyncBackEnvelope,
  deriveFigmaCompanionSyncBackEvidence,
  type FigmaCompanionCheckpointInput,
} from "@/features/openpencil-figma/figma-companion-syncback";
import { compareOpenPencilRevisionVisuals } from "@/features/openpencil-figma/openpencil-revision-visual-diff";
import {
  downloadOpenPencilSourceFile,
  requestOpenPencilFigRevision,
  requestOpenPencilRuntimeProof,
  requestOpenPencilVisualJudgement,
} from "@/features/openpencil-figma/openpencil-figma-client";
import {
  cacheOpenPencilSourceWorkspaceLocally,
  clearOpenPencilSourceWorkspaceFromCurrentProject,
  ensureOpenPencilSourceWorkspaceLoaded,
  OPENPENCIL_SOURCE_WORKSPACE_SETTINGS_KEY,
  readOpenPencilSourceWorkspaceFromSettings,
  saveOpenPencilSourceWorkspaceToCurrentProject,
  serializeOpenPencilSourceWorkspaceForPersistence,
} from "@/features/openpencil-figma/openpencil-source-workspace-storage";
import { activateOpenPencilSourceWorkspace } from "@/features/openpencil-figma/openpencil-source-navigation";
import {
  captureCogoDesignMutationElements,
  cogoDesignMutationValuesEqual,
  computeCogoProjectSha256,
  finalizeOpenPencilCogoDesignMutation,
  prepareOpenPencilCogoDesignMutation,
  proposeOpenPencilCogoDesignMutation,
  restoreCogoDesignMutationElements,
  type CogoDesignMutationElementReplacement,
  type OpenPencilCogoElementSnapshot,
  type OpenPencilCogoDesignMutationDraftOperation,
} from "@/features/openpencil-figma/openpencil-cogo-design-mutation";
import { useEditorStore } from "@/features/page-editor/store/editor-store";
import { findElementInTree } from "@/features/page-editor/store/canvas-slice";
import type { LoadedApp } from "@/features/page-editor/store/types";
import { buildUiFieldPayload } from "@/store/auto-save-payload";
import { withEditorAutoSaveSuppressed } from "@/store/auto-save-bridge";
import {
  flushSaves,
  hasPendingChanges,
  withAutoSaveDispatchLock,
} from "@/store/auto-save";
import { useStudioStore } from "@/store/studio-store";
import { getBlockBaseline } from "@/services/block-baselines";
import { getProject, saveBlock, saveProjectMeta } from "@/services/project-api";
import { markTemplateDivergenceOnForeignEdit } from "@/features/templates/content-form/project-lineage";
import { consumeStudioCapabilityApprovalGrant } from "@/features/chat/services/studio-capability-approval-grant";
import {
  transitionSessionOnlyDesignMutationAuthority,
  withCoordinatedOpenPencilWorkspaceWrite,
  withOpenPencilDesignMutationGuard,
} from "@/features/openpencil-figma/openpencil-design-mutation-guard";

const stringSchema = { type: "string" } as const;
const booleanSchema = { type: "boolean" } as const;
const objectSchema = { type: "object" } as const;

type OpenPencilFigEditOperationName =
  OpenPencilFigEditOperation["operation"];
type OpenPencilNodeFigEditOperationName = Exclude<
  OpenPencilFigEditOperationName,
  "set-variable-mode-value"
>;

const openPencilNodeEditBaseProperties = {
  nodeId: stringSchema,
  sourceNodeId: stringSchema,
  expectedNodeType: {
    type: "string",
    enum: [...OPENPENCIL_EDITABLE_NODE_TYPES],
  },
  expectedName: stringSchema,
} satisfies Record<string, JsonSchema>;

const openPencilHexColorSchema: JsonSchema = {
  type: "string",
  pattern: "^#[0-9a-fA-F]{6}(?:[0-9a-fA-F]{2})?$",
};

const openPencilVariableModeValueSchema: JsonSchema = {
  oneOf: [
    stringSchema,
    { type: "number" },
    booleanSchema,
    {
      type: "object",
      required: ["aliasId"],
      properties: { aliasId: stringSchema },
      additionalProperties: false,
    },
  ],
};

function openPencilNodeEditOperationSchema(
  operation: OpenPencilNodeFigEditOperationName,
  required: string[],
  properties: Record<string, JsonSchema>,
  constraints: Pick<JsonSchema, "anyOf"> = {},
): JsonSchema {
  return {
    type: "object",
    required: ["operation", "nodeId", "expectedNodeType", ...required],
    properties: {
      operation: { type: "string", const: operation },
      ...openPencilNodeEditBaseProperties,
      ...properties,
    },
    additionalProperties: false,
    ...constraints,
  };
}

const openPencilFigEditOperationSchemas = {
  "rename-node": openPencilNodeEditOperationSchema(
    "rename-node",
    ["name"],
    { name: stringSchema },
  ),
  "set-text": openPencilNodeEditOperationSchema("set-text", ["text"], {
    text: stringSchema,
  }),
  "set-visible": openPencilNodeEditOperationSchema(
    "set-visible",
    ["visible"],
    { visible: booleanSchema },
  ),
  "set-opacity": openPencilNodeEditOperationSchema(
    "set-opacity",
    ["opacity"],
    { opacity: { type: "number", minimum: 0, maximum: 1 } },
  ),
  "set-solid-fill": openPencilNodeEditOperationSchema(
    "set-solid-fill",
    ["fillIndex"],
    {
      fillIndex: { type: "integer", minimum: 0, maximum: 99 },
      expectedColor: openPencilHexColorSchema,
      color: openPencilHexColorSchema,
      fillOpacity: { type: "number", minimum: 0, maximum: 1 },
    },
    {
      anyOf: ["color", "fillOpacity"].map((field) => ({
        required: [field],
      })),
    },
  ),
  "set-geometry": openPencilNodeEditOperationSchema(
    "set-geometry",
    [],
    {
      x: { type: "number", minimum: -1_000_000, maximum: 1_000_000 },
      y: { type: "number", minimum: -1_000_000, maximum: 1_000_000 },
      width: { type: "number", exclusiveMinimum: 0, maximum: 1_000_000 },
      height: { type: "number", exclusiveMinimum: 0, maximum: 1_000_000 },
      rotation: { type: "number", minimum: -360, maximum: 360 },
    },
    {
      anyOf: ["x", "y", "width", "height", "rotation"].map((field) => ({
        required: [field],
      })),
    },
  ),
  "set-variable-mode-value": {
    type: "object",
    required: [
      "operation",
      "variableId",
      "collectionId",
      "modeId",
      "expectedVariableType",
      "value",
    ],
    properties: {
      operation: { type: "string", const: "set-variable-mode-value" },
      variableId: stringSchema,
      collectionId: stringSchema,
      modeId: stringSchema,
      expectedVariableType: {
        type: "string",
        enum: [...OPENPENCIL_VARIABLE_TYPES],
      },
      expectedValue: openPencilVariableModeValueSchema,
      value: openPencilVariableModeValueSchema,
    },
    additionalProperties: false,
  },
} satisfies Record<OpenPencilFigEditOperationName, JsonSchema>;

const openPencilFigEditOperationInputSchema: JsonSchema = {
  oneOf: Object.values(openPencilFigEditOperationSchemas),
};

const openPencilCogoDesignMutationKindSet = {
  "rename-node": true,
  "set-text": true,
  "set-visible": true,
  "set-opacity": true,
  "set-geometry": true,
  "set-solid-fill": true,
} satisfies Record<DesignMutationKind, true>;

const openPencilCogoDesignMutationKinds = Object.keys(
  openPencilCogoDesignMutationKindSet,
) as DesignMutationKind[];

function recordValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
const openPencilSourceBundleSchema: JsonSchema = {
  type: "object",
  required: [
    "schemaVersion",
    "status",
    "sourceId",
    "sourceName",
    "fileName",
    "generatedAt",
    "readback",
    "gates",
    "generationAdmission",
  ],
  properties: {
    schemaVersion: {
      type: "string",
      const: "cogo.studio.openpencil-figma-source-bundle.v2",
    },
    status: { type: "string", enum: ["ready", "blocked"] },
    sourceId: stringSchema,
    sourceName: stringSchema,
    fileName: stringSchema,
    generatedAt: stringSchema,
    gates: { type: "array", items: objectSchema },
    generationAdmission: {
      type: "object",
      required: ["status", "reasonCodes", "requiredGateIds", "evaluatedAt"],
      properties: {
        status: { type: "string", enum: ["blocked", "admitted"] },
        reasonCodes: { type: "array", items: stringSchema },
        requiredGateIds: { type: "array", items: stringSchema },
        evaluatedAt: stringSchema,
      },
    },
  },
};
type IdePreviewInputType = Extract<
  CogoXyzInputType,
  "html" | "react" | "markdown"
>;
type OpenAiImportInputType = Extract<
  CogoXyzInputType,
  "prompt" | "markdown" | "html" | "react" | "cogo_json"
>;
type ImportHubSourceInputType = Extract<
  CogoXyzInputType,
  "prompt" | "markdown" | "html" | "react"
>;
type UrlFetchStatus = "fetched" | "failed" | "skipped";

interface ResolvedImportHubInput {
  inputType: CogoXyzInputType;
  sourceText: string;
  metadata: Record<string, unknown>;
}

function basename(filePath: string): string {
  return (
    filePath.split(/[\\/]/).filter(Boolean).pop() || filePath || "Untitled"
  );
}

function inputTypeForIdeFile(filePath: string): IdePreviewInputType {
  const lower = filePath.toLowerCase();
  if (lower.endsWith(".html") || lower.endsWith(".htm")) return "html";
  if (lower.endsWith(".md") || lower.endsWith(".mdx")) return "markdown";
  if (
    lower.endsWith(".tsx") ||
    lower.endsWith(".jsx") ||
    lower.endsWith(".ts") ||
    lower.endsWith(".js")
  )
    return "react";
  return "markdown";
}

async function importOpenAiInput(
  input: Pick<CogoXyzCreateOpenAiCogoAppInput, "title" | "input" | "inputType">,
) {
  if (input.inputType === "cogo_json")
    return importCogoJsonAppInput({ title: input.title, input: input.input });
  if (input.inputType === "prompt")
    return generatePromptAppInput({ title: input.title, input: input.input });
  return importSourceAppInput({
    title: input.title,
    input: input.input,
    inputType: input.inputType,
  });
}

function sourceTypeForImportHub(
  input: Pick<CogoXyzImportCogoAssetInput, "importKind" | "sourceUrl">,
): CogoXyzCreateCogoAppInput["sourceType"] {
  if (input.importKind === "url" || input.sourceUrl) return "url_import";
  if (input.importKind === "prompt") return "assistant_prompt";
  return "manual";
}

function inputTypeForImportHub(
  importKind: CogoXyzImportCogoAssetInput["importKind"],
): CogoXyzInputType {
  if (importKind === "url") return "markdown";
  return importKind;
}

function sourceTextForImportHub(input: CogoXyzImportCogoAssetInput): string {
  if (input.importKind !== "url") return input.input;
  const url = input.sourceUrl || input.input;
  return [
    `# ${input.title}`,
    "",
    `Source URL: ${url}`,
    "",
    input.input === url
      ? "URL import captured. Content extraction is pending connector fetch proof."
      : input.input,
  ].join("\n");
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function truncateSource(value: string, limit = 12_000): string {
  return value.length > limit
    ? `${value.slice(0, limit)}\n\n[truncated ${value.length - limit} chars]`
    : value;
}

function cleanPageText(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .replace(/\u0000/g, "")
    .trim();
}

function extractHtmlMarkdown(
  html: string,
  sourceUrl: string,
  fallbackTitle: string,
): {
  title: string;
  description?: string;
  body: string;
} {
  if (typeof DOMParser === "undefined") {
    return {
      title: fallbackTitle,
      body: cleanPageText(
        html
          .replace(/<script[\s\S]*?<\/script>/gi, "")
          .replace(/<style[\s\S]*?<\/style>/gi, "")
          .replace(/<[^>]+>/g, " "),
      ),
    };
  }
  const document = new DOMParser().parseFromString(html, "text/html");
  document
    .querySelectorAll("script, style, noscript, svg")
    .forEach((node) => node.remove());
  const title =
    cleanPageText(document.querySelector("title")?.textContent ?? "") ||
    fallbackTitle;
  const description = cleanPageText(
    document
      .querySelector('meta[name="description"]')
      ?.getAttribute("content") ??
      document
        .querySelector('meta[property="og:description"]')
        ?.getAttribute("content") ??
      "",
  );
  const body = cleanPageText(document.body?.textContent ?? html);
  return {
    title,
    description: description || undefined,
    body,
  };
}

async function fetchUrlImportSource(
  input: CogoXyzImportCogoAssetInput,
): Promise<ResolvedImportHubInput> {
  const sourceUrl = input.sourceUrl || input.input;
  const fallback = sourceTextForImportHub(input);
  if (!isHttpUrl(sourceUrl)) {
    return {
      inputType: "markdown",
      sourceText: fallback,
      metadata: {
        urlFetchStatus: "skipped" satisfies UrlFetchStatus,
        urlFetchReason: "invalid_or_non_http_url",
      },
    };
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(sourceUrl, {
      method: "GET",
      redirect: "follow",
      signal: controller.signal,
      headers: {
        Accept: "text/html,text/markdown,text/plain,*/*",
      },
    });
    const contentType = response.headers.get("content-type") ?? "";
    const raw = truncateSource(await response.text());
    if (!response.ok) {
      return {
        inputType: "markdown",
        sourceText: [
          fallback,
          "",
          `Fetch status: ${response.status}`,
          raw ? `\nFetched body excerpt:\n\n${raw}` : "",
        ].join("\n"),
        metadata: {
          urlFetchStatus: "failed" satisfies UrlFetchStatus,
          urlFetchHttpStatus: response.status,
          urlFetchContentType: contentType,
          urlFetchLength: raw.length,
        },
      };
    }
    const isHtml =
      contentType.includes("text/html") || /<\/?[a-z][\s\S]*>/i.test(raw);
    if (isHtml) {
      const extracted = extractHtmlMarkdown(raw, sourceUrl, input.title);
      return {
        inputType: "markdown",
        sourceText: [
          `# ${extracted.title}`,
          "",
          `Source URL: ${sourceUrl}`,
          extracted.description ? `\n${extracted.description}` : "",
          "",
          truncateSource(extracted.body),
        ]
          .filter(Boolean)
          .join("\n"),
        metadata: {
          urlFetchStatus: "fetched" satisfies UrlFetchStatus,
          urlFetchHttpStatus: response.status,
          urlFetchContentType: contentType,
          urlFetchLength: raw.length,
          extractedTitle: extracted.title,
          extractedDescription: extracted.description,
        },
      };
    }
    return {
      inputType: "markdown",
      sourceText: [
        `# ${input.title}`,
        "",
        `Source URL: ${sourceUrl}`,
        "",
        raw,
      ].join("\n"),
      metadata: {
        urlFetchStatus: "fetched" satisfies UrlFetchStatus,
        urlFetchHttpStatus: response.status,
        urlFetchContentType: contentType,
        urlFetchLength: raw.length,
      },
    };
  } catch (err) {
    return {
      inputType: "markdown",
      sourceText: fallback,
      metadata: {
        urlFetchStatus: "failed" satisfies UrlFetchStatus,
        urlFetchError: err instanceof Error ? err.message : String(err),
      },
    };
  } finally {
    window.clearTimeout(timeout);
  }
}

async function resolveImportHubInput(
  input: CogoXyzImportCogoAssetInput,
): Promise<ResolvedImportHubInput> {
  if (input.importKind === "url") return fetchUrlImportSource(input);
  const inputType = inputTypeForImportHub(input.importKind);
  return {
    inputType,
    sourceText: sourceTextForImportHub(input),
    metadata: {},
  };
}

async function importHubInput(
  input: CogoXyzImportCogoAssetInput,
  resolved: ResolvedImportHubInput,
) {
  const normalizedInputType = resolved.inputType;
  if (normalizedInputType === "cogo_json")
    return importCogoJsonAppInput({
      title: input.title,
      input: resolved.sourceText,
    });
  if (normalizedInputType === "prompt")
    return generatePromptAppInput({
      title: input.title,
      input: resolved.sourceText,
    });
  return importSourceAppInput({
    title: input.title,
    input: resolved.sourceText,
    inputType: normalizedInputType as ImportHubSourceInputType,
  });
}

function importedAppMetadata(
  imported: Awaited<ReturnType<typeof importOpenAiInput>>,
): Record<string, unknown> {
  return {
    importedProjectId: imported.projectId,
    importedProjectName: imported.name,
    ...("metadata" in imported &&
    imported.metadata &&
    typeof imported.metadata === "object"
      ? imported.metadata
      : {}),
  };
}

async function saveCurrentCogoXyzRegistry(): Promise<void> {
  const state = useStudioStore.getState();
  const projectId =
    state.projectSource === "server" ? state.currentProjectId : null;
  const snapshot = cogoXyzRegistry.snapshot();
  try {
    if (!projectId) {
      await saveCogoXyzRegistryToCurrentProject();
      return;
    }
    await cogoXyzRegistrySyncCoordinator.saveSnapshot(
      projectId,
      snapshot,
      async () => {
        const saved = await saveCogoXyzRegistryToCurrentProject();
        if (!saved) throw new Error("COGO_XYZ_ACTIVE_PROJECT_NOT_AVAILABLE");
      },
    );
  } catch (err) {
    console.warn(
      "[cogo-xyz] failed to save registry snapshot to project settings",
      err,
    );
    throw err;
  }
}

async function loadCurrentCogoXyzRegistry(): Promise<void> {
  const state = useStudioStore.getState();
  if (state.projectSource !== "server") return;
  const projectId = state.currentProjectId;
  const previousSnapshot = cogoXyzRegistry.snapshot();
  let loaded = false;
  try {
    loaded = await loadCogoXyzRegistryFromCurrentProject();
  } catch (err) {
    cogoXyzRegistry.restore(previousSnapshot);
    console.warn(
      "[cogo-xyz] failed to load registry snapshot from project settings",
      err,
    );
    return;
  }
  const current = useStudioStore.getState();
  if (
    current.projectSource !== "server" ||
    current.currentProjectId !== projectId
  ) {
    cogoXyzRegistry.reset();
    throw new Error("COGO_XYZ_ACTIVE_PROJECT_CHANGED_DURING_REGISTRY_LOAD");
  }
  if (!loaded) cogoXyzRegistry.reset();
}

function requestedRegistryProjectId(payload: Record<string, unknown>): string | null {
  const value = payload.registryProjectId
  return typeof value === "string" && value.trim() ? value.trim() : null
}

type RequestedRegistryScope = {
  projectId: string | null;
  previousSnapshot: ReturnType<typeof cogoXyzRegistry.snapshot> | null;
};

async function loadRequestedCogoXyzRegistry(
  payload: Record<string, unknown>,
): Promise<RequestedRegistryScope> {
  const projectId = requestedRegistryProjectId(payload)
  if (!projectId) {
    await loadCurrentCogoXyzRegistry()
    return { projectId: null, previousSnapshot: null }
  }
  const previousSnapshot = cogoXyzRegistry.snapshot()
  const loaded = await loadCogoXyzRegistryFromProject(projectId)
  if (!loaded) {
    cogoXyzRegistry.restore(previousSnapshot)
    throw new Error(`COGO_XYZ_REGISTRY_PROJECT_NOT_AVAILABLE:${projectId}`)
  }
  return { projectId, previousSnapshot }
}

async function saveRequestedCogoXyzRegistry(
  scope: RequestedRegistryScope,
): Promise<void> {
  if (!scope.projectId) {
    await saveCurrentCogoXyzRegistry()
    return
  }
  const scopedSnapshot = cogoXyzRegistry.snapshot()
  try {
    await cogoXyzRegistrySyncCoordinator.saveSnapshot(
      scope.projectId,
      scopedSnapshot,
      async () => {
        await saveCogoXyzRegistryToProject(scope.projectId!, scopedSnapshot)
      },
    )
  } catch (error) {
    cogoXyzRegistry.restore(scope.previousSnapshot ?? scopedSnapshot)
    throw error
  }
  cogoXyzRegistry.restore(
    mergeCogoXyzRegistrySnapshots([
      scope.previousSnapshot ?? scopedSnapshot,
      scopedSnapshot,
    ]),
  )
}

async function assertCurrentSourceGenerationAdmitted(): Promise<void> {
  const workspace = await ensureOpenPencilSourceWorkspaceLoaded();
  if (!workspace && currentProjectSourceAuthority()) {
    throw new Error(
      "The source-derived application is missing its authority workspace. Restore the source evidence before exporting its Coogo JSON candidate.",
    );
  }
  assertOpenPencilGenerationAdmitted(workspace?.bundle ?? null);
}

async function assertCurrentSourceReleaseAdmitted(): Promise<void> {
  const workspace = await ensureOpenPencilSourceWorkspaceLoaded();
  if (!workspace && currentProjectSourceAuthority()) {
    throw new Error(
      "The source-derived application is missing its authority workspace. Restore the source evidence before export, publish, or deploy.",
    );
  }
  assertOpenPencilSourceReleaseAdmitted(workspace);
}

async function currentSourceWorkspace() {
  const workspace = await ensureOpenPencilSourceWorkspaceLoaded();
  if (!workspace) throw new Error("No OpenPencil source workspace is active.");
  return workspace;
}

async function commitFigmaCompanionWorkspaceMetadata(
  previous: OpenPencilSourceWorkspace,
  next: OpenPencilSourceWorkspace,
): Promise<void> {
  const state = useStudioStore.getState();
  const projectId = state.currentProjectId;
  if (!projectId) {
    throw new Error("FIGMA_COMPANION_ACTIVE_PROJECT_REQUIRED");
  }
  if (state.projectSource !== "server") {
    transitionSessionOnlyDesignMutationAuthority({
      projectId,
      previousWorkspaceHash: computeSourceWorkspaceHash(previous),
      nextWorkspaceHash: computeSourceWorkspaceHash(next),
    });
  }
  withCoordinatedOpenPencilWorkspaceWrite(() =>
    useStudioStore.getState().setOpenPencilSourceWorkspace(next),
  );
  if (state.projectSource === "server") {
    try {
      await saveOpenPencilSourceWorkspaceToCurrentProject(next);
    } catch (error) {
      cacheOpenPencilSourceWorkspaceLocally(previous, projectId);
      if (useStudioStore.getState().currentProjectId === projectId) {
        withCoordinatedOpenPencilWorkspaceWrite(() =>
          useStudioStore.getState().setOpenPencilSourceWorkspace(previous),
        );
      }
      throw error;
    }
  }
}

function activeSourceWorkspace() {
  return useStudioStore.getState().openPencilSourceWorkspace;
}

/**
 * The project-metadata marker that says "this project has a registered
 * design source" — independent of whether the in-memory workspace happens to
 * be loaded right now. Exported so other MCP registration modules can apply
 * the same `!workspace && currentProjectSourceAuthority()` fail-closed guard
 * this file already uses below, instead of re-deriving it.
 */
export function currentProjectSourceAuthority(): Record<string, unknown> | null {
  const project = useStudioStore.getState().currentProject;
  if (!project || typeof project !== "object" || Array.isArray(project)) {
    return null;
  }
  const metadata = (project as unknown as Record<string, unknown>).metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  const authority = (metadata as Record<string, unknown>).sourceAuthority;
  if (!authority || typeof authority !== "object" || Array.isArray(authority)) {
    return null;
  }
  return (authority as Record<string, unknown>).schemaVersion ===
    "cogo.openpencil-source-authority.v1"
    ? (authority as Record<string, unknown>)
    : null;
}

function assertGenericOperationHasNoActiveSource(operation: string): void {
  if (!activeSourceWorkspace() && !currentProjectSourceAuthority()) return;
  throw new Error(
    `${operation} cannot bypass the active .fig source authority. Use cogo.studio.create_openpencil_cogo_candidate.`,
  );
}

function sameStringSet(left: string[], right: string[]): boolean {
  const leftValues = [...new Set(left)].sort();
  const rightValues = [...new Set(right)].sort();
  return (
    leftValues.length === rightValues.length &&
    leftValues.every((value, index) => value === rightValues[index])
  );
}

async function sha256Text(value: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function canonicalCandidatePreviewUrl(value: string): string {
  const base =
    typeof window === "undefined"
      ? "https://www.cogo.xyz"
      : window.location.origin;
  const url = new URL(value, base);
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error("Source candidate preview URL must use HTTP or HTTPS.");
  }
  return url.toString();
}

function validateOpenPencilCandidateJson(
  input: string,
  workspace: Awaited<ReturnType<typeof currentSourceWorkspace>>,
): void {
  const manifest = workspace.assemblyManifest;
  if (!manifest || manifest.status !== "validated") {
    throw new Error(
      "OpenPencil candidate requires a validated assembly manifest.",
    );
  }
  let candidate: Record<string, unknown>;
  try {
    const parsed = JSON.parse(input) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      throw new Error("candidate must be an object");
    }
    candidate = parsed as Record<string, unknown>;
  } catch (error) {
    throw new Error(
      `Invalid OpenPencil candidate JSON: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
  const metadata =
    candidate.metadata && typeof candidate.metadata === "object"
      ? (candidate.metadata as Record<string, unknown>)
      : null;
  const authority =
    metadata?.sourceAuthority && typeof metadata.sourceAuthority === "object"
      ? (metadata.sourceAuthority as Record<string, unknown>)
      : null;
  if (
    authority?.schemaVersion !== "cogo.openpencil-source-authority.v1" ||
    authority.sourceId !== workspace.sourceId ||
    authority.assemblyManifestId !== manifest.manifestId ||
    authority.sourceRevision !== manifest.sourceRevision ||
    authority.finalArtifactWriter !== "deterministic-cogo-compiler" ||
    authority.mayInjectMissingUi !== false ||
    authority.sourceAuthorityViolationCount !== 0
  ) {
    throw new Error(
      "OpenPencil candidate sourceAuthority metadata is missing or does not match the active assembly.",
    );
  }
  const selectedPageIds = Array.isArray(authority.selectedPageIds)
    ? authority.selectedPageIds.map(String)
    : [];
  const selectedSurfaceIds = Array.isArray(authority.selectedSurfaceIds)
    ? authority.selectedSurfaceIds.map(String)
    : [];
  const selectedTokenIds = Array.isArray(authority.selectedTokenIds)
    ? authority.selectedTokenIds.map(String)
    : [];
  if (
    !sameStringSet(selectedPageIds, manifest.selectedPageIds) ||
    !sameStringSet(selectedSurfaceIds, manifest.selectedSurfaceIds) ||
    !sameStringSet(selectedTokenIds, manifest.selectedTokenIds)
  ) {
    throw new Error(
      "OpenPencil candidate selected source IDs do not match the assembly manifest.",
    );
  }
  const appManifest =
    candidate.manifest && typeof candidate.manifest === "object"
      ? (candidate.manifest as Record<string, unknown>)
      : null;
  const pages = Array.isArray(appManifest?.pages)
    ? appManifest.pages.filter((page): page is Record<string, unknown> =>
        Boolean(page && typeof page === "object" && !Array.isArray(page)),
      )
    : [];
  const coveredSurfaceIds = pages.flatMap((page) =>
    Array.isArray(page.sourceSurfaceIds)
      ? page.sourceSurfaceIds.map(String)
      : [],
  );
  if (!sameStringSet(coveredSurfaceIds, manifest.selectedSurfaceIds)) {
    throw new Error(
      "OpenPencil candidate pages do not cover the selected source surfaces exactly.",
    );
  }
}

function sourceWorkspaceInverse(
  workspace: ReturnType<
    typeof useStudioStore.getState
  >["openPencilSourceWorkspace"],
) {
  return {
    tool: "cogo.studio.restore_openpencil_source_workspace",
    params: workspace ? { workspace } : { clear: true },
  };
}

function parseDesignMutationElementReplacements(
  value: unknown,
): CogoDesignMutationElementReplacement[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error("Design mutation restore requires element snapshots.");
  }
  return value.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new Error("Invalid design mutation element snapshot.");
    }
    const snapshot = item as Record<string, unknown>;
    if (
      typeof snapshot.blockName !== "string" ||
      typeof snapshot.elementId !== "string" ||
      !snapshot.element ||
      typeof snapshot.element !== "object" ||
      Array.isArray(snapshot.element)
    ) {
      throw new Error("Invalid design mutation element snapshot.");
    }
    return {
      blockName: snapshot.blockName,
      elementId: snapshot.elementId,
      element: structuredClone(snapshot.element as Record<string, unknown>),
    };
  });
}

function parseDesignMutationDraftOperations(
  value: unknown,
): OpenPencilCogoDesignMutationDraftOperation[] {
  if (!Array.isArray(value) || value.length === 0) {
    throw new Error("Design mutation proposal requires bounded operations.");
  }
  return value.map((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) {
      throw new Error("Invalid design mutation draft operation.");
    }
    const operation = item as Record<string, unknown>;
    const sourceTarget = operation.sourceTarget;
    const cogoTarget = operation.cogoTarget;
    if (
      typeof operation.operationId !== "string" ||
      typeof operation.kind !== "string" ||
      !sourceTarget ||
      typeof sourceTarget !== "object" ||
      Array.isArray(sourceTarget) ||
      !cogoTarget ||
      typeof cogoTarget !== "object" ||
      Array.isArray(cogoTarget) ||
      !("nextValue" in operation)
    ) {
      throw new Error("Invalid design mutation draft operation.");
    }
    return operation as unknown as OpenPencilCogoDesignMutationDraftOperation;
  });
}

async function resolvePersistedFigmaDesignSourceReference(workItemId: string) {
  await loadCurrentCogoXyzRegistry();
  const packet = cogoXyzRegistry.getEvidencePacket(workItemId);
  if (!packet || packet.workItem.sourceType !== "figma_frame") {
    throw new Error("FIGMA_COMPANION_PERSISTED_FIGMA_IMPORT_REQUIRED");
  }
  const currentProjectId = useStudioStore.getState().currentProjectId;
  if (!currentProjectId || packet.workItem.workspaceId !== currentProjectId) {
    throw new Error("FIGMA_COMPANION_FIGMA_IMPORT_WORKSPACE_MISMATCH");
  }
  const metadata = recordValue(packet.workItem.sourceRef.metadata);
  const fileKey = metadata.figmaFileId;
  const nodeId = metadata.figmaNodeId;
  const url = packet.workItem.sourceRef.url ?? metadata.figmaUrl;
  if (
    typeof fileKey !== "string" ||
    typeof nodeId !== "string" ||
    typeof url !== "string"
  ) {
    throw new Error("FIGMA_COMPANION_PERSISTED_FIGMA_PROVENANCE_INCOMPLETE");
  }
  return {
    kind: "figma-reference" as const,
    fileKey,
    nodeId,
    url,
    observedAt: packet.workItem.createdAt,
  };
}

function parseFigmaCompanionCheckpoint(
  value: unknown,
): FigmaCompanionCheckpointInput {
  const checkpoint = recordValue(value);
  if (
    typeof checkpoint.fileKey !== "string" ||
    typeof checkpoint.version !== "string" ||
    typeof checkpoint.hash !== "string" ||
    !Array.isArray(checkpoint.nodeIds) ||
    !checkpoint.nodeIds.every((nodeId) => typeof nodeId === "string") ||
    !checkpoint.nodeAuthorities ||
    typeof checkpoint.nodeAuthorities !== "object" ||
    Array.isArray(checkpoint.nodeAuthorities)
  ) {
    throw new Error("INVALID_FIGMA_COMPANION_CHECKPOINT");
  }
  return {
    fileKey: checkpoint.fileKey,
    version: checkpoint.version,
    hash: checkpoint.hash,
    nodeIds: checkpoint.nodeIds as string[],
    nodeAuthorities:
      checkpoint.nodeAuthorities as FigmaCompanionCheckpointInput["nodeAuthorities"],
  };
}

function assertEditorDesignMutationTargets(
  project: LoadedApp,
  snapshots: OpenPencilCogoElementSnapshot[],
): void {
  const editor = useEditorStore.getState();
  const blockNames = new Set(snapshots.map((snapshot) => snapshot.blockName));
  if (blockNames.size !== 1) {
    throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
  }
  for (const blockName of blockNames) {
    const editorBlock = editor.blocks[blockName];
    const projectBlock = project.blocks[blockName];
    if (
      !editorBlock ||
      !projectBlock ||
      !cogoDesignMutationValuesEqual(editorBlock.ui.root, projectBlock.ui.root)
    ) {
      throw new Error("DESIGN_MUTATION_EDITOR_BLOCK_DIVERGED");
    }
  }
  for (const snapshot of snapshots) {
    const block = editor.blocks[snapshot.blockName];
    const element = block
      ? findElementInTree(block.ui.root, snapshot.elementId)
      : undefined;
    if (!element) {
      throw new Error("DESIGN_MUTATION_EDITOR_TARGET_NOT_LOADED");
    }
    if (!cogoDesignMutationValuesEqual(element, snapshot.before)) {
      throw new Error("DESIGN_MUTATION_EDITOR_TARGET_DIVERGED");
    }
  }
}

function commitCogoDesignMutationProject(
  project: LoadedApp,
  snapshots: OpenPencilCogoElementSnapshot[],
  isDirty: boolean,
): void {
  const blockName = snapshots[0]?.blockName;
  if (
    !blockName ||
    snapshots.some((snapshot) => snapshot.blockName !== blockName) ||
    !project.blocks[blockName]
  ) {
    throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
  }
  withEditorAutoSaveSuppressed(() => {
    useEditorStore.setState((editor) => {
      const block = editor.blocks[blockName];
      if (!block) throw new Error("DESIGN_MUTATION_COMMIT_TARGET_MISSING");
      block.ui = structuredClone(project.blocks[blockName].ui);
      const projectBlock = project.blocks[blockName] as unknown as Record<
        string,
        unknown
      >;
      const projectUi = projectBlock.ui as Record<string, unknown> | undefined;
      if ("root" in projectBlock) {
        (block as unknown as Record<string, unknown>).root = structuredClone(
          projectBlock.root ?? projectUi?.root,
        );
      }
    });
  });
  useStudioStore.setState({ currentProject: project, isDirty });
}

function carriedDecisionCount<T>(
  previous: Record<string, T>,
  next: Record<string, T>,
): number {
  return Object.keys(next).filter((id) =>
    Object.prototype.hasOwnProperty.call(previous, id),
  ).length;
}

function computeSourceWorkspaceHash(
  workspace: OpenPencilSourceWorkspace,
): string {
  return computeDesignContentHash(JSON.parse(JSON.stringify(workspace)));
}

type DesignMutationPersistenceResult = {
  localSourceWorkspace: "cached" | "unavailable" | "session-only";
  serverSourceWorkspace: boolean;
  cogoBlockSave: "persisted" | "session-only";
};

async function getMatchingServerSourceProject(
  projectId: string,
  expectedWorkspace: OpenPencilSourceWorkspace,
) {
  const serverProject = await getProject(projectId);
  if (!serverProject) {
    throw new Error("DESIGN_MUTATION_SERVER_PROJECT_READ_FAILED");
  }
  const serverWorkspace = await readOpenPencilSourceWorkspaceFromSettings(
    serverProject.settings,
    {
      projectId,
      tenantId: useStudioStore.getState().session?.user?.id,
      principalId: useStudioStore.getState().session?.user?.id,
    },
  );
  if (
    !serverWorkspace ||
    !cogoDesignMutationValuesEqual(serverWorkspace, expectedWorkspace)
  ) {
    throw new Error("DESIGN_MUTATION_SERVER_SOURCE_WORKSPACE_DIVERGED");
  }
  return serverProject;
}

async function saveDesignMutationBlockUi(input: {
  projectId: string;
  blockId: string;
  project: LoadedApp;
  blockName: string;
}): Promise<void> {
  const updates = {
    ui: buildUiFieldPayload(
      input.project.blocks[input.blockName] as unknown as Record<
        string,
        unknown
      >,
    ),
  };
  const baseline = getBlockBaseline(input.blockId);
  if (baseline) {
    await saveBlock(input.projectId, input.blockId, updates, { baseline });
  } else {
    await saveBlock(input.projectId, input.blockId, updates);
  }
  // Plan B2 Task 5 (review finding: third direct saveBlock caller, uncovered
  // by the original two-call-site wiring) — this OpenPencil design-mutation
  // UI save bypasses `queueSave` exactly like `apply-slot-values.ts` and
  // `block-editor-canvas.tsx`'s `handleSave`, so it needs the same
  // divergedAt boundary-exit hook for uniform coverage. The hook's own
  // guards (templateFormApplyInFlight, lineage presence, already-diverged,
  // current-project match) make this a safe no-op for the common case (a
  // template instance has no `openPencilSourceWorkspace`, so this whole
  // design-mutation flow is unreachable for it in practice) and for the
  // compensating rollback call below (which only runs after the primary
  // save already fired this once, so the guard's already-diverged check
  // short-circuits it).
  markTemplateDivergenceOnForeignEdit(input.projectId);
}

async function persistCoordinatedDesignMutation(input: {
  projectId: string;
  nextProject: LoadedApp;
  previousProject: LoadedApp;
  nextWorkspace: OpenPencilSourceWorkspace;
  previousWorkspace: OpenPencilSourceWorkspace;
  blockName: string;
}): Promise<DesignMutationPersistenceResult> {
  const state = useStudioStore.getState();
  if (
    state.currentProjectId !== input.projectId ||
    state.currentProjectId === null
  ) {
    throw new Error("DESIGN_MUTATION_PERSISTENCE_PROJECT_DIVERGED");
  }
  if (state.projectSource !== "server") {
    transitionSessionOnlyDesignMutationAuthority({
      projectId: input.projectId,
      previousWorkspaceHash: computeSourceWorkspaceHash(
        input.previousWorkspace,
      ),
      nextWorkspaceHash: computeSourceWorkspaceHash(input.nextWorkspace),
    });
    return {
      localSourceWorkspace: "session-only",
      serverSourceWorkspace: false,
      cogoBlockSave: "session-only",
    };
  }
  const blockId = state.blockIdMap[input.blockName];
  if (!blockId) {
    throw new Error("DESIGN_MUTATION_SERVER_BLOCK_ID_REQUIRED");
  }
  const serverProject = await getMatchingServerSourceProject(
    input.projectId,
    input.previousWorkspace,
  );
  const previousSettings = structuredClone(recordValue(serverProject.settings));
  const nextApplicationMetadata = {
    ...recordValue(previousSettings.cogoApplicationMetadata),
  };
  const nextDesignMutationRevision = recordValue(
    input.nextProject.metadata,
  ).designMutationRevision;
  if (nextDesignMutationRevision === undefined) {
    delete nextApplicationMetadata.designMutationRevision;
  } else {
    nextApplicationMetadata.designMutationRevision = nextDesignMutationRevision;
  }
  const nextSettings = {
    ...previousSettings,
    [OPENPENCIL_SOURCE_WORKSPACE_SETTINGS_KEY]:
      serializeOpenPencilSourceWorkspaceForPersistence(input.nextWorkspace),
    cogoApplicationMetadata: nextApplicationMetadata,
  };
  let blockPersisted = false;
  let settingsStarted = false;
  try {
    await saveDesignMutationBlockUi({
      projectId: input.projectId,
      blockId,
      project: input.nextProject,
      blockName: input.blockName,
    });
    blockPersisted = true;
    settingsStarted = true;
    await saveProjectMeta(input.projectId, { settings: nextSettings });
    const localCached = cacheOpenPencilSourceWorkspaceLocally(
      input.nextWorkspace,
      input.projectId,
    );
    return {
      localSourceWorkspace: localCached ? "cached" : "unavailable",
      serverSourceWorkspace: true,
      cogoBlockSave: "persisted",
    };
  } catch (error) {
    const rollbackErrors: unknown[] = [];
    if (settingsStarted) {
      try {
        await saveProjectMeta(input.projectId, { settings: previousSettings });
      } catch (rollbackError) {
        rollbackErrors.push(rollbackError);
      }
    }
    if (blockPersisted) {
      try {
        await saveDesignMutationBlockUi({
          projectId: input.projectId,
          blockId,
          project: input.previousProject,
          blockName: input.blockName,
        });
      } catch (rollbackError) {
        rollbackErrors.push(rollbackError);
      }
    }
    cacheOpenPencilSourceWorkspaceLocally(
      input.previousWorkspace,
      input.projectId,
    );
    if (rollbackErrors.length > 0) {
      throw new AggregateError(
        [error, ...rollbackErrors],
        "DESIGN_MUTATION_PARTIAL_PERSISTENCE_ROLLBACK_FAILED",
      );
    }
    throw error;
  }
}

function designMutationRestoreInverse(input: {
  projectId: string;
  workspace: ReturnType<
    typeof useStudioStore.getState
  >["openPencilSourceWorkspace"];
  project: LoadedApp;
  expectedWorkspace: OpenPencilSourceWorkspace;
  expectedProject: LoadedApp;
  targets: Array<{ blockName: string; elementId: string }>;
}) {
  return {
    tool: "cogo.studio.restore_openpencil_cogo_design_mutation",
    params: {
      projectId: input.projectId,
      expectedWorkspaceHash: computeSourceWorkspaceHash(
        input.expectedWorkspace,
      ),
      expectedProjectSha256: computeCogoProjectSha256(input.expectedProject),
      workspace: input.workspace,
      projectMetadataPresent: input.project.metadata !== undefined,
      projectMetadata: structuredClone(input.project.metadata ?? {}),
      elements: captureCogoDesignMutationElements(input.project, input.targets),
    },
  };
}

export function registerCogoXyzTools(mcpBus: StudioMcpBus): void {
  mcpBus.registerTool("restore_openpencil_source_workspace", {
    description:
      "Internal reversible restore boundary for OpenPencil source-workspace revisions.",
    metadata: { tags: ["openpencil", "source-workspace", "undo", "internal"] },
    inputSchema: {
      type: "object",
      properties: {
        workspace: objectSchema,
        clear: booleanSchema,
      },
    },
    handler: async (payload) => {
      const previous = useStudioStore.getState().openPencilSourceWorkspace;
      if (payload.clear === true) {
        await clearOpenPencilSourceWorkspaceFromCurrentProject();
      } else {
        const workspace = await normalizePersistedOpenPencilSourceWorkspace(
          payload.workspace,
          {
            projectId: useStudioStore.getState().currentProjectId,
            tenantId: useStudioStore.getState().session?.user?.id,
            principalId: useStudioStore.getState().session?.user?.id,
          },
        );
        if (!workspace)
          throw new Error("Invalid source workspace restore payload.");
        useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
        await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      }
      return {
        restored: true,
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("restore_openpencil_cogo_design_mutation", {
    description:
      "Internal reversible restore boundary for a coordinated OpenPencil and Coogo JSON DesignMutation.",
    metadata: {
      tags: [
        "confirmation.required",
        "write",
        "user-approval",
        "openpencil",
        "design-mutation",
        "cogo-json",
        "undo",
        "internal",
      ],
    },
    inputSchema: {
      type: "object",
      required: [
        "projectId",
        "expectedWorkspaceHash",
        "expectedProjectSha256",
        "workspace",
        "projectMetadataPresent",
        "projectMetadata",
        "elements",
      ],
      properties: {
        projectId: stringSchema,
        expectedWorkspaceHash: stringSchema,
        expectedProjectSha256: stringSchema,
        workspace: objectSchema,
        projectMetadataPresent: booleanSchema,
        projectMetadata: objectSchema,
        elements: {
          type: "array",
          minItems: 1,
          items: objectSchema,
        },
      },
    },
    handler: async (payload) => {
      return withOpenPencilDesignMutationGuard(() =>
        withAutoSaveDispatchLock(async () => {
          const state = useStudioStore.getState();
          if (
            !state.currentProject ||
            !state.currentProjectId ||
            payload.projectId !== state.currentProjectId
          ) {
            throw new Error("Design mutation restore project mismatch.");
          }
          if (hasPendingChanges() || state.saveConflict || state.saveError) {
            throw new Error("DESIGN_MUTATION_PENDING_OR_FAILED_SAVE");
          }
          if (
            !state.openPencilSourceWorkspace ||
            payload.expectedWorkspaceHash !==
              computeSourceWorkspaceHash(state.openPencilSourceWorkspace) ||
            payload.expectedProjectSha256 !==
              computeCogoProjectSha256(state.currentProject)
          ) {
            throw new Error("DESIGN_MUTATION_UNDO_STATE_DIVERGED");
          }
          const workspace = await normalizePersistedOpenPencilSourceWorkspace(
            payload.workspace,
            {
              projectId: state.currentProjectId,
              tenantId: state.session?.user?.id,
              principalId: state.session?.user?.id,
            },
          );
          if (!workspace) {
            throw new Error(
              "Invalid design mutation source workspace restore.",
            );
          }
          const replacements = parseDesignMutationElementReplacements(
            payload.elements,
          );
          const targets = replacements.map(({ blockName, elementId }) => ({
            blockName,
            elementId,
          }));
          const currentElements = captureCogoDesignMutationElements(
            state.currentProject,
            targets,
          );
          assertEditorDesignMutationTargets(
            state.currentProject,
            currentElements.map((current) => ({
              blockName: current.blockName,
              elementId: current.elementId,
              pointer: "",
              before: current.element,
              after: current.element,
            })),
          );
          const restoredProject = restoreCogoDesignMutationElements({
            project: state.currentProject,
            replacements,
            metadata:
              payload.projectMetadataPresent === true &&
              payload.projectMetadata &&
              typeof payload.projectMetadata === "object" &&
              !Array.isArray(payload.projectMetadata)
                ? (payload.projectMetadata as Record<string, unknown>)
                : undefined,
          });
          const restoreSnapshots = replacements.map((replacement) => ({
            blockName: replacement.blockName,
            elementId: replacement.elementId,
            pointer: "",
            before: replacement.element,
            after: replacement.element,
          }));
          const previousWorkspace = state.openPencilSourceWorkspace
            ? structuredClone(state.openPencilSourceWorkspace)
            : null;
          if (!previousWorkspace) {
            throw new Error(
              "Design mutation restore requires an active workspace.",
            );
          }
          const blockName = replacements[0]?.blockName;
          if (
            !blockName ||
            replacements.some(
              (replacement) => replacement.blockName !== blockName,
            )
          ) {
            throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
          }
          const previousProject = structuredClone(state.currentProject);
          const inverse = designMutationRestoreInverse({
            projectId: state.currentProjectId,
            workspace: previousWorkspace,
            project: previousProject,
            expectedWorkspace: workspace,
            expectedProject: restoredProject,
            targets,
          });
          let persistence: DesignMutationPersistenceResult | null = null;
          try {
            persistence = await persistCoordinatedDesignMutation({
              projectId: state.currentProjectId,
              nextProject: restoredProject,
              previousProject,
              nextWorkspace: workspace,
              previousWorkspace,
              blockName,
            });
            const postPersistState = useStudioStore.getState();
            if (
              hasPendingChanges() ||
              postPersistState.currentProjectId !== state.currentProjectId ||
              !postPersistState.currentProject ||
              computeCogoProjectSha256(postPersistState.currentProject) !==
                payload.expectedProjectSha256 ||
              !postPersistState.openPencilSourceWorkspace ||
              computeSourceWorkspaceHash(
                postPersistState.openPencilSourceWorkspace,
              ) !== payload.expectedWorkspaceHash
            ) {
              throw new Error("DESIGN_MUTATION_COMMIT_SCOPE_DIVERGED");
            }
            commitCogoDesignMutationProject(
              restoredProject,
              restoreSnapshots,
              state.projectSource === "server" ? state.isDirty : true,
            );
            withCoordinatedOpenPencilWorkspaceWrite(() =>
              useStudioStore.getState().setOpenPencilSourceWorkspace(workspace),
            );
          } catch (error) {
            if (persistence?.cogoBlockSave === "persisted") {
              try {
                await persistCoordinatedDesignMutation({
                  projectId: state.currentProjectId,
                  nextProject: previousProject,
                  previousProject: restoredProject,
                  nextWorkspace: previousWorkspace,
                  previousWorkspace: workspace,
                  blockName,
                });
              } catch (rollbackError) {
                throw new AggregateError(
                  [error, rollbackError],
                  "DESIGN_MUTATION_UNDO_POST_PERSISTENCE_ROLLBACK_FAILED",
                );
              }
            } else if (persistence?.cogoBlockSave === "session-only") {
              transitionSessionOnlyDesignMutationAuthority({
                projectId: state.currentProjectId,
                previousWorkspaceHash: computeSourceWorkspaceHash(workspace),
                nextWorkspaceHash:
                  computeSourceWorkspaceHash(previousWorkspace),
              });
            }
            throw error;
          }
          if (!persistence) {
            throw new Error("DESIGN_MUTATION_PERSISTENCE_RESULT_MISSING");
          }
          return {
            restored: true,
            activeSourceId: workspace.sourceId,
            persistence,
            immutableRevisionArtifactDeleted: false,
            _inverse: inverse,
          };
        }),
      );
    },
  });

  mcpBus.registerTool("create_cogo_app", {
    description:
      "cogo.xyz Phase 1: create a WorkItem, Coogo bundle record, preview link, and evidence from prompt/code input.",
    metadata: { tags: ["cogo-xyz", "phase-1", "preview"] },
    inputSchema: {
      type: "object",
      required: [
        "workspaceId",
        "title",
        "sourceType",
        "inputType",
        "input",
        "target",
      ],
      properties: {
        workspaceId: stringSchema,
        title: stringSchema,
        sourceType: {
          type: "string",
          enum: [
            "assistant_prompt",
            "github_pr",
            "github_repo",
            "github_commit",
            "ide_file",
            "url_import",
            "manual",
            "template_remix",
          ],
        },
        inputType: {
          type: "string",
          enum: ["prompt", "markdown", "html", "react", "cogo_json"],
        },
        input: stringSchema,
        target: {
          type: "string",
          enum: ["landing_page", "dashboard", "admin", "creator_page", "app"],
        },
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("create_cogo_app");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzCreateCogoAppInput;
      const imported =
        input.inputType === "cogo_json"
          ? await importPersistedCogoJsonAppInput({
              title: input.title,
              input: input.input,
            })
          : input.inputType === "prompt"
            ? await generatePromptAppInput({
                title: input.title,
                input: input.input,
              })
            : input.inputType === "html" ||
                input.inputType === "react" ||
                input.inputType === "markdown"
              ? await importSourceAppInput({
                  title: input.title,
                  input: input.input,
                  inputType: input.inputType,
                })
              : null;
      const registryInput =
        imported && input.inputType === "prompt"
          ? { ...input, workspaceId: imported.projectId }
          : input;
      const result = cogoXyzRegistry.createCogoApp(
        registryInput,
        imported
          ? {
              bundleRef: imported.bundleRef,
              previewUrl: imported.previewUrl,
              metadata: {
                importedProjectId: imported.projectId,
                importedProjectName: imported.name,
                importSource: input.inputType,
                ...("metadata" in imported &&
                imported.metadata &&
                typeof imported.metadata === "object"
                  ? imported.metadata
                  : {}),
              },
            }
          : undefined,
      );
      const generatedSnapshot = cogoXyzRegistry.snapshot();
      const registryPersistence =
        imported &&
        (input.inputType === "prompt" || input.inputType === "cogo_json")
          ? await cogoXyzRegistrySyncCoordinator.persistGeneratedSnapshot(
              imported.projectId,
              generatedSnapshot,
              () =>
                persistGeneratedCogoXyzRegistryToProject(
                  imported.projectId,
                  generatedSnapshot,
                ),
            )
          : null;
      if (!registryPersistence) await saveCurrentCogoXyzRegistry();
      return {
        workItemId: result.workItemId,
        bundleId: result.bundleId,
        previewUrl: result.previewUrl,
        evidenceId: result.evidenceId,
        status: result.status,
        ...(imported ? { projectId: imported.projectId } : {}),
        ...(registryPersistence ? { registryPersistence } : {}),
      };
    },
  });

  mcpBus.registerTool("retry_cogo_xyz_registry_sync", {
    description:
      "Retry a pending generated-project Library registry settings overlay without regenerating the application.",
    metadata: { tags: ["cogo-xyz", "library", "retry"] },
    inputSchema: {
      type: "object",
      required: ["projectId"],
      properties: { projectId: stringSchema },
    },
    handler: async (payload) => {
      const projectId = String(payload.projectId);
      return cogoXyzRegistrySyncCoordinator.retryPendingSnapshot(
        projectId,
        (snapshot, expectedSettingsRevision) =>
          persistGeneratedCogoXyzRegistryToProject(projectId, snapshot, {
            expectedSettingsRevision,
          }),
      );
    },
  });

  mcpBus.registerTool("create_openpencil_cogo_candidate", {
    description:
      "Create a source-derived Coogo JSON candidate only when its deterministic compiler metadata and page/surface coverage exactly match the active OpenPencil assembly.",
    metadata: {
      tags: [
        "figma",
        "openpencil",
        "source-authority",
        "candidate",
        "cogo-json",
      ],
    },
    inputSchema: {
      type: "object",
      required: ["workspaceId", "title", "input", "target"],
      properties: {
        workspaceId: stringSchema,
        title: stringSchema,
        input: stringSchema,
        target: {
          type: "string",
          enum: ["landing_page", "dashboard", "admin", "creator_page", "app"],
        },
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      const workspace = await currentSourceWorkspace();
      const candidateInput = String(payload.input);
      validateOpenPencilCandidateJson(candidateInput, workspace);
      const inputSha256 = await sha256Text(candidateInput);
      await loadCurrentCogoXyzRegistry();
      const input: CogoXyzCreateCogoAppInput = {
        workspaceId: String(payload.workspaceId),
        title: String(payload.title),
        sourceType: "manual",
        inputType: "cogo_json",
        input: candidateInput,
        target: payload.target as CogoXyzCreateCogoAppInput["target"],
        ...(typeof payload.createdBy === "string"
          ? { createdBy: payload.createdBy }
          : {}),
      };
      const imported = await importCogoJsonAppInput({
        title: input.title,
        input: input.input,
        preserveOpenPencilAuthority: true,
      });
      const assembly = workspace.assemblyManifest!;
      const result = cogoXyzRegistry.createCogoApp(input, {
        bundleRef: imported.bundleRef,
        previewUrl: imported.previewUrl,
        metadata: {
          importedProjectId: imported.projectId,
          importedProjectName: imported.name,
          importSource: "openpencil_source_assembly",
          sourceId: workspace.sourceId,
          sourceRevision: assembly.sourceRevision,
          assemblyManifestId: assembly.manifestId,
          selectedPageIds: assembly.selectedPageIds,
          selectedSurfaceIds: assembly.selectedSurfaceIds,
          selectedTokenIds: assembly.selectedTokenIds,
        },
      });
      const previewUrl = canonicalCandidatePreviewUrl(result.previewUrl);
      const boundWorkspace = recordOpenPencilSourceCandidateBinding(workspace, {
        candidateId: result.workItemId,
        manifestId: assembly.manifestId,
        sourceRevision: assembly.sourceRevision,
        inputSha256,
        applicationArtifactRef: imported.bundleRef,
        previewUrl,
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(boundWorkspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(boundWorkspace);
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: result.workItemId,
        bundleId: result.bundleId,
        bundleRef: imported.bundleRef,
        previewUrl,
        evidenceId: result.evidenceId,
        status: result.status,
        sourceId: workspace.sourceId,
        assemblyManifestId: assembly.manifestId,
      };
    },
  });

  mcpBus.registerTool("create_openai_cogo_app", {
    description:
      "Library Hub: create a Coogo preview/share/gallery launch package from ChatGPT, Custom GPT, or OpenAI API output.",
    metadata: {
      tags: ["library-hub", "openai", "chatgpt", "assistant", "share"],
    },
    inputSchema: {
      type: "object",
      required: [
        "workspaceId",
        "title",
        "source",
        "inputType",
        "input",
        "target",
      ],
      properties: {
        workspaceId: stringSchema,
        title: stringSchema,
        source: {
          type: "string",
          enum: ["chatgpt", "custom_gpt", "openai_api"],
        },
        inputType: {
          type: "string",
          enum: ["prompt", "markdown", "html", "react", "cogo_json"],
        },
        input: stringSchema,
        target: {
          type: "string",
          enum: ["landing_page", "dashboard", "admin", "creator_page", "app"],
        },
        model: stringSchema,
        conversationUrl: stringSchema,
        audience: stringSchema,
        tags: { type: "array", items: stringSchema },
        visibility: { type: "string", enum: ["public", "unlisted"] },
        publishGallery: booleanSchema,
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("create_openai_cogo_app");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzCreateOpenAiCogoAppInput;
      const visibility = input.visibility ?? "unlisted";
      const imported = await importOpenAiInput({
        title: input.title,
        input: input.input,
        inputType: input.inputType as OpenAiImportInputType,
      });
      const importedMetadata = importedAppMetadata(imported);
      const openAiMetadata = {
        importSource: "openai_assistant",
        assistantSource: input.source,
        model: input.model,
        conversationUrl: input.conversationUrl,
        audience: input.audience,
        tags: input.tags ?? [],
        inputLength: input.input.length,
      };
      const created = cogoXyzRegistry.createCogoApp(
        {
          workspaceId: input.workspaceId,
          title: input.title,
          sourceType: "assistant_prompt",
          inputType: input.inputType,
          input: input.input,
          target: input.target,
          createdBy: input.createdBy,
        },
        {
          bundleRef: imported.bundleRef,
          previewUrl: imported.previewUrl,
          toolName: "create_openai_cogo_app",
          metadata: {
            ...openAiMetadata,
            ...importedMetadata,
          },
        },
      );
      created.workItem.sourceRef = {
        ...created.workItem.sourceRef,
        url: input.conversationUrl,
        metadata: {
          ...created.workItem.sourceRef.metadata,
          ...openAiMetadata,
          ...importedMetadata,
        },
      };
      const shared = cogoXyzRegistry.shareCogoPreview({
        previewUrl: created.previewUrl,
        visibility,
        createdBy: input.createdBy,
      });
      const gallery = input.publishGallery
        ? cogoXyzRegistry.publishGalleryItem({
            workItemId: created.workItemId,
            visibility,
            title: input.title,
            audience: input.audience,
            tags: input.tags,
            createdBy: input.createdBy,
          })
        : null;
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: created.workItemId,
        bundleId: created.bundleId,
        previewUrl: created.previewUrl,
        shareUrl: shared.shareUrl,
        evidenceIds: [
          created.evidenceId,
          shared.evidenceId,
          ...(gallery ? [gallery.evidenceId] : []),
        ],
        galleryUrl: gallery?.galleryUrl,
        remixUrl: gallery?.remixUrl,
        shareText: gallery?.shareText,
        madeWithCogoBadge: gallery?.madeWithCogoBadge,
        publicAttribution: gallery?.publicAttribution,
        status: "launch_package_created",
      };
    },
  });

  mcpBus.registerTool("import_cogo_asset", {
    description:
      "Library Hub: import URL, markdown, HTML, React, prompt, or Coogo JSON into a previewable Coogo asset with optional share/gallery kit.",
    metadata: {
      tags: [
        "library-hub",
        "import",
        "url",
        "markdown",
        "html",
        "react",
        "cogo-json",
      ],
    },
    inputSchema: {
      type: "object",
      required: ["workspaceId", "title", "importKind", "input", "target"],
      properties: {
        workspaceId: stringSchema,
        title: stringSchema,
        importKind: {
          type: "string",
          enum: ["url", "markdown", "html", "react", "cogo_json", "prompt"],
        },
        input: stringSchema,
        target: {
          type: "string",
          enum: ["landing_page", "dashboard", "admin", "creator_page", "app"],
        },
        sourceUrl: stringSchema,
        audience: stringSchema,
        tags: { type: "array", items: stringSchema },
        visibility: { type: "string", enum: ["public", "unlisted"] },
        publishGallery: booleanSchema,
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("import_cogo_asset");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzImportCogoAssetInput;
      const sourceType = sourceTypeForImportHub(input);
      const resolved = await resolveImportHubInput(input);
      const inputType = resolved.inputType;
      const sourceText = resolved.sourceText;
      const imported = await importHubInput(input, resolved);
      const importedMetadata = importedAppMetadata(imported);
      const created = cogoXyzRegistry.createCogoApp(
        {
          workspaceId: input.workspaceId,
          title: input.title,
          sourceType,
          inputType,
          input: sourceText,
          target: input.target,
          createdBy: input.createdBy,
        },
        {
          bundleRef: imported.bundleRef,
          previewUrl: imported.previewUrl,
          toolName: "import_cogo_asset",
          metadata: {
            importSource: "library_hub_import",
            importKind: input.importKind,
            sourceUrl:
              input.sourceUrl ||
              (input.importKind === "url" ? input.input : undefined),
            audience: input.audience,
            tags: input.tags ?? [],
            inputLength: input.input.length,
            ...resolved.metadata,
            ...importedMetadata,
          },
        },
      );
      created.workItem.sourceRef = {
        ...created.workItem.sourceRef,
        url:
          input.sourceUrl ||
          (input.importKind === "url" ? input.input : undefined),
        metadata: {
          ...created.workItem.sourceRef.metadata,
          importSource: "library_hub_import",
          importKind: input.importKind,
          sourceUrl:
            input.sourceUrl ||
            (input.importKind === "url" ? input.input : undefined),
          audience: input.audience,
          tags: input.tags ?? [],
          inputLength: input.input.length,
          ...resolved.metadata,
          ...importedMetadata,
        },
      };
      const evidenceIds = [created.evidenceId];
      const visibility = input.visibility ?? "unlisted";
      const shared = visibility
        ? cogoXyzRegistry.shareCogoPreview({
            previewUrl: created.previewUrl,
            visibility,
            createdBy: input.createdBy,
          })
        : null;
      if (shared) evidenceIds.push(shared.evidenceId);
      const gallery = input.publishGallery
        ? cogoXyzRegistry.publishGalleryItem({
            workItemId: created.workItemId,
            visibility,
            title: input.title,
            audience: input.audience,
            tags: input.tags,
            createdBy: input.createdBy,
          })
        : null;
      if (gallery) evidenceIds.push(gallery.evidenceId);
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: created.workItemId,
        bundleId: created.bundleId,
        previewUrl: created.previewUrl,
        shareUrl: shared?.shareUrl,
        evidenceIds,
        galleryUrl: gallery?.galleryUrl,
        remixUrl: gallery?.remixUrl,
        shareText: gallery?.shareText,
        madeWithCogoBadge: gallery?.madeWithCogoBadge,
        publicAttribution: gallery?.publicAttribution,
        importKind: input.importKind,
        sourceType,
        inputType,
        urlFetchStatus: resolved.metadata.urlFetchStatus,
        status: "imported_to_library",
      };
    },
  });

  mcpBus.registerTool("import_github_pr_preview", {
    description:
      "cogo.xyz Phase 2: read GitHub PR metadata/files and create a Coogo preview evidence packet without external writes.",
    metadata: { tags: ["cogo-xyz", "phase-2", "github", "read-only"] },
    inputSchema: {
      type: "object",
      required: ["workspaceId", "repo", "prNumber"],
      properties: {
        workspaceId: stringSchema,
        repo: stringSchema,
        prNumber: { type: "integer" },
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("import_github_pr_preview");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzGithubPrPreviewInput;
      const context = await fetchGithubPrContext(input);
      const markdown = githubPrContextToMarkdown(context);
      const imported = await importSourceAppInput({
        title: `${context.repo} PR ${context.prNumber}`,
        input: markdown,
        inputType: "markdown",
      });
      const result = cogoXyzRegistry.createCogoApp(
        {
          workspaceId: input.workspaceId,
          title: context.title,
          sourceType: "github_pr",
          inputType: "markdown",
          input: markdown,
          target: "app",
          createdBy: input.createdBy,
        },
        {
          bundleRef: imported.bundleRef,
          previewUrl: imported.previewUrl,
          metadata: {
            importedProjectId: imported.projectId,
            importedProjectName: imported.name,
            importSource: "github_pr",
            repo: context.repo,
            prNumber: context.prNumber,
            prUrl: context.url,
            branch: context.branch,
            baseBranch: context.baseBranch,
            commitSha: context.commitSha,
            changedFiles: context.changedFiles,
            uiRelatedFiles: context.uiRelatedFiles,
          },
        },
      );
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: result.workItemId,
        bundleId: result.bundleId,
        previewUrl: result.previewUrl,
        evidenceId: result.evidenceId,
        status: result.status,
        repo: context.repo,
        prNumber: context.prNumber,
        commitSha: context.commitSha,
        changedFiles: context.changedFiles,
        uiRelatedFiles: context.uiRelatedFiles,
      };
    },
  });

  mcpBus.registerTool("preview_ide_file", {
    description:
      "cogo.xyz Phase 6: create a Coogo preview from the current Cursor/VS Code file or selection.",
    metadata: { tags: ["cogo-xyz", "phase-6", "ide", "cursor", "vscode"] },
    inputSchema: {
      type: "object",
      required: ["workspaceId", "filePath", "content"],
      properties: {
        workspaceId: stringSchema,
        filePath: stringSchema,
        content: stringSchema,
        framework: stringSchema,
        route: stringSchema,
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("preview_ide_file");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzPreviewIdeFileInput;
      const inputType = inputTypeForIdeFile(input.filePath);
      const title = basename(input.filePath);
      const imported = await importSourceAppInput({
        title,
        input: input.content,
        inputType,
      });
      const result = cogoXyzRegistry.createCogoApp(
        {
          workspaceId: input.workspaceId,
          title,
          sourceType: "ide_file",
          inputType,
          input: input.content,
          target: "app",
          createdBy: input.createdBy,
        },
        {
          bundleRef: imported.bundleRef,
          previewUrl: imported.previewUrl,
          toolName: "preview_ide_file",
          metadata: {
            importSource: "ide_file",
            sourceFile: input.filePath,
            framework: input.framework,
            route: input.route,
            importedProjectId: imported.projectId,
            importedProjectName: imported.name,
            detectedInputType: inputType,
          },
        },
      );
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: result.workItemId,
        bundleId: result.bundleId,
        previewUrl: result.previewUrl,
        evidenceId: result.evidenceId,
        status: result.status,
        sourceFile: input.filePath,
        framework: input.framework,
        route: input.route,
      };
    },
  });

  mcpBus.registerTool("update_cogo_app", {
    description:
      "cogo.xyz Phase 2: record a Coogo app update instruction, create a new bundle/preview, and preserve diff evidence.",
    metadata: { tags: ["cogo-xyz", "phase-2", "update"] },
    inputSchema: {
      type: "object",
      required: ["workItemId", "instruction", "patchMode", "createPreview"],
      properties: {
        workItemId: stringSchema,
        instruction: stringSchema,
        patchMode: { type: "string", enum: ["safe", "direct"] },
        createPreview: booleanSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("update_cogo_app");
      await loadCurrentCogoXyzRegistry();
      const result = cogoXyzRegistry.updateCogoApp(
        payload as unknown as CogoXyzUpdateCogoAppInput,
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("inspect_cogo_preview", {
    description:
      "cogo.xyz Phase 1: record preview inspection evidence. Browser execution remains a separate proof gate.",
    metadata: { tags: ["cogo-xyz", "phase-1", "evidence"] },
    inputSchema: {
      type: "object",
      required: ["previewUrl", "checks"],
      properties: {
        previewUrl: stringSchema,
        checks: {
          type: "array",
          items: {
            type: "string",
            enum: ["screenshot", "console", "clickable", "mobile", "desktop"],
          },
        },
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzInspectPreviewInput;
      const proofResult = await runCogoXyzPreviewProof(input);
      const result = cogoXyzRegistry.inspectPreview(input, proofResult);
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("validate_figma_token", {
    description:
      "cogo.xyz Studio: validate and store a user-provided Figma token on the server before any Figma source import.",
    metadata: { tags: ["cogo-xyz", "figma", "auth"] },
    inputSchema: {
      type: "object",
      required: ["figmaAccessToken"],
      properties: {
        figmaAccessToken: stringSchema,
        workspaceId: stringSchema,
      },
    },
    handler: async (payload) => {
      const input = payload as {
        figmaAccessToken?: string;
        workspaceId?: string;
      };
      return storeFigmaAccessTokenOnServer(input.figmaAccessToken ?? "", {
        workspaceId: input.workspaceId,
      });
    },
  });

  mcpBus.registerTool("import_openpencil_figma_source", {
    description:
      "cogo.xyz Studio: register an OpenPencil .fig source-fact bundle and open the Studio Sources rail. This does not generate Coogo JSON.",
    metadata: { tags: ["cogo-xyz", "figma", "openpencil", "source-facts"] },
    inputSchema: {
      type: "object",
      required: ["sourceBundle"],
      properties: {
        sourceBundle: openPencilSourceBundleSchema,
        openInSourceSurface: booleanSchema,
      },
    },
    handler: async (payload) => {
      const input = payload as {
        sourceBundle?: unknown;
        openInSourceSurface?: boolean;
      };
      const previous = useStudioStore.getState().openPencilSourceWorkspace;
      const bundle = normalizeOpenPencilFigmaSourceBundle(input.sourceBundle);
      const store = useStudioStore.getState();
      store.setOpenPencilFigmaSource(bundle);
      await saveOpenPencilSourceWorkspaceToCurrentProject(
        useStudioStore.getState().openPencilSourceWorkspace,
      );
      if (input.openInSourceSurface !== false) {
        activateOpenPencilSourceWorkspace();
      }
      return {
        ...summarizeOpenPencilFigmaSource(bundle),
        cogoJsonGeneration: bundle.generationAdmission.status,
        gates: bundle.gates,
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("inspect_openpencil_figma_source", {
    description:
      "cogo.xyz Studio: inspect the current OpenPencil .fig source bundle, source-fact counts, and generation boundary.",
    metadata: { tags: ["cogo-xyz", "figma", "openpencil", "source-facts"] },
    inputSchema: { type: "object", properties: {} },
    handler: async () => {
      await ensureOpenPencilSourceWorkspaceLoaded();
      const bundle = useStudioStore.getState().openPencilFigmaSource;
      return {
        ...summarizeOpenPencilFigmaSource(bundle),
        cogoJsonGeneration: bundle?.generationAdmission.status ?? "blocked",
        gates: bundle?.gates ?? [],
        claimBoundary: bundle?.claimBoundary ?? null,
        workspace: summarizeOpenPencilSourceWorkspace(
          useStudioStore.getState().openPencilSourceWorkspace,
        ),
      };
    },
  });

  mcpBus.registerTool("export_openpencil_source_file", {
    description:
      "cogo.xyz Studio: download the checksum-verified current retained .fig or .pen source. The current source may be an original import or an immutable edited revision.",
    metadata: {
      readOnly: true,
      tags: ["cogo-xyz", "figma", "openpencil", "export"],
    },
    inputSchema: { type: "object", properties: {} },
    handler: async () => {
      await ensureOpenPencilSourceWorkspaceLoaded();
      const bundle = useStudioStore.getState().openPencilFigmaSource;
      if (!bundle) throw new Error("No OpenPencil source is loaded.");
      return {
        sourceId: bundle.sourceId,
        ...(await downloadOpenPencilSourceFile(bundle)),
        exportKind: bundle.revisionLineage
          ? "checksum-verified-revision"
          : "checksum-verified-original",
      };
    },
  });

  mcpBus.registerTool("revise_openpencil_figma_source", {
    description:
      "Apply bounded, confirmed node or variable edits to the retained .fig source, export a new immutable revision, and re-run OpenPencil source readback. This resets mapping, visual, assembly, candidate, and runtime-proof decisions.",
    metadata: {
      tags: ["figma", "openpencil", "source-edit", "immutable-revision"],
    },
    inputSchema: {
      type: "object",
      required: ["reason", "operations"],
      properties: {
        patchId: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
        reason: stringSchema,
        operations: {
          type: "array",
          minItems: 1,
          maxItems: 100,
          items: openPencilFigEditOperationInputSchema,
        },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      if (previous.candidateBinding || currentProjectSourceAuthority()) {
        throw new Error(
          "A source-derived Coogo candidate is already bound. Open a new project or restore the pre-candidate source workspace before revising the .fig source.",
        );
      }
      if (!previous.bundle.fileSha256) {
        throw new Error("Source revision requires a retained source checksum.");
      }
      const patch = OpenPencilFigEditPatchSchema.parse({
        schemaVersion: OPENPENCIL_FIG_EDIT_PATCH_SCHEMA,
        patchId:
          typeof payload.patchId === "string"
            ? payload.patchId
            : `patch-${crypto.randomUUID()}`,
        baseSourceId: previous.bundle.sourceId,
        baseSha256: previous.bundle.fileSha256,
        actor: payload.actor === "human" ? "human" : "agent",
        reason: payload.reason,
        operations: payload.operations,
        requestedAt: new Date().toISOString(),
      });
      const result = await requestOpenPencilFigRevision({
        bundle: previous.bundle,
        patch,
      });
      const revisionVisualDiff = await compareOpenPencilRevisionVisuals(
        previous.bundle,
        result.bundle,
      );
      const workspace = recordSourceRevisionVisualDiff(
        createOpenPencilSourceWorkspace(result.bundle),
        revisionVisualDiff,
      );
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      activateOpenPencilSourceWorkspace();
      return {
        source: summarizeOpenPencilFigmaSource(result.bundle),
        revisionLineage: result.bundle.revisionLineage,
        revisionVisualDiff,
        evidence: result.evidence,
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        cogoJsonGeneration: result.bundle.generationAdmission.status,
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("bind_openpencil_figma_companion_upstream", {
    description:
      "Bind the active OpenPencil workspace to Figma provenance already persisted by a successful Studio Figma-frame import. Raw file keys, node IDs, and URLs are never accepted as caller authority.",
    metadata: {
      tags: ["write", "figma", "openpencil", "provenance", "sync-back"],
    },
    inputSchema: {
      type: "object",
      required: ["workItemId"],
      properties: { workItemId: stringSchema },
    },
    handler: async (payload) => {
      const workspace = await currentSourceWorkspace();
      const upstream = await resolvePersistedFigmaDesignSourceReference(
        String(payload.workItemId),
      );
      const next = bindOpenPencilFigmaCompanionUpstream(
        workspace,
        upstream,
        new Date().toISOString(),
      );
      await commitFigmaCompanionWorkspaceMetadata(workspace, next);
      return {
        upstreamFigma: next.figmaCompanion.upstreamFigma,
        authorityMode: "persisted-studio-figma-import",
      };
    },
  });

  mcpBus.registerTool("propose_openpencil_cogo_design_mutation", {
    description:
      "Read the active source and Coogo element targets, derive canonical JSON pointers and current values, and return a frozen content-addressed DesignMutation proposal without writing either authority.",
    metadata: {
      readOnly: true,
      tags: [
        "figma",
        "openpencil",
        "design-mutation",
        "cogo-json",
        "preflight",
      ],
    },
    inputSchema: {
      type: "object",
      required: ["reason", "operations"],
      properties: {
        mutationId: stringSchema,
        reason: stringSchema,
        evidenceRefs: { type: "array", items: stringSchema },
        operations: {
          type: "array",
          minItems: 1,
          maxItems: 100,
          items: {
            type: "object",
            required: [
              "operationId",
              "kind",
              "sourceTarget",
              "cogoTarget",
              "nextValue",
            ],
            properties: {
              operationId: stringSchema,
              kind: {
                type: "string",
                enum: openPencilCogoDesignMutationKinds,
              },
              sourceTarget: objectSchema,
              cogoTarget: objectSchema,
              nextValue: {} as JsonSchema,
            },
          },
        },
      },
    },
    handler: async (payload) => {
      const workspace = await currentSourceWorkspace();
      const state = useStudioStore.getState();
      if (!state.currentProject || !state.currentProjectId) {
        throw new Error("Design mutation proposal requires an active project.");
      }
      const proposal = proposeOpenPencilCogoDesignMutation({
        mutationId:
          typeof payload.mutationId === "string"
            ? payload.mutationId
            : `mutation-${crypto.randomUUID()}`,
        actorId: "cogo-studio-agent",
        reason: String(payload.reason),
        operations: parseDesignMutationDraftOperations(payload.operations),
        evidenceRefs: Array.isArray(payload.evidenceRefs)
          ? payload.evidenceRefs.map(String)
          : [],
        requestedAt: new Date().toISOString(),
        workspace,
        currentProjectId: state.currentProjectId,
        currentProject: state.currentProject,
      });
      return {
        proposal,
        applyOperationId: "source.figma.apply_design_mutation",
        requiresConfirmation: true,
        changes: proposal.operations.map((operation) => ({
          operationId: operation.operationId,
          kind: operation.kind,
          path: operation.cogoTarget.jsonPointer,
          before: operation.cogoTarget.expectedValue,
          proposed: operation.nextValue,
        })),
        invalidates: {
          visualJudgements: Object.keys(workspace.visualJudgements).length,
          revisionVisualDiff: Boolean(workspace.revisionVisualDiff),
          assemblyManifest: Boolean(workspace.assemblyManifest),
          candidateBinding: Boolean(workspace.candidateBinding),
          runtimeProof: Boolean(workspace.runtimeProof),
        },
      };
    },
  });

  mcpBus.registerTool("apply_openpencil_cogo_design_mutation", {
    description:
      "After explicit user confirmation, apply one content-addressed DesignMutation to a new immutable source revision and the exact bound Coogo JSON elements through a client-coordinated sequence with compensating rollback.",
    metadata: {
      tags: [
        "confirmation.required",
        "write",
        "user-approval",
        "figma",
        "openpencil",
        "design-mutation",
        "cogo-json",
      ],
    },
    inputSchema: {
      type: "object",
      required: ["proposal"],
      properties: {
        proposal: objectSchema,
      },
    },
    handler: async (payload) => {
      return withOpenPencilDesignMutationGuard(async () => {
        const initialState = useStudioStore.getState();
        if (!initialState.currentProject || !initialState.currentProjectId) {
          throw new Error("Design mutation requires an active Coogo project.");
        }
        const approval = consumeStudioCapabilityApprovalGrant({
          token: payload._capabilityApprovalToken,
          tool: "cogo.studio.apply_openpencil_cogo_design_mutation",
          params: payload,
          projectId: initialState.currentProjectId,
        });
        const previousWorkspace = structuredClone(
          await currentSourceWorkspace(),
        );
        const mutation = approveDesignMutation(payload.proposal as never, {
          actor: { kind: "human", id: approval.actorId },
          approvedAt: approval.approvedAt,
        });
        const previousProject = structuredClone(initialState.currentProject);
        let plan = prepareOpenPencilCogoDesignMutation({
          mutation,
          workspace: previousWorkspace,
          currentProjectId: initialState.currentProjectId,
          currentProject: initialState.currentProject,
        });
        assertEditorDesignMutationTargets(
          initialState.currentProject,
          plan.elementSnapshots,
        );
        const blockName = plan.elementSnapshots[0]?.blockName;
        if (!blockName) {
          throw new Error("DESIGN_MUTATION_SINGLE_BLOCK_SCOPE_REQUIRED");
        }
        if (
          initialState.projectSource === "server" &&
          !initialState.blockIdMap[blockName]
        ) {
          throw new Error("DESIGN_MUTATION_SERVER_BLOCK_ID_REQUIRED");
        }
        // Drain known edits and fail before creating an immutable source
        // artifact when the current local/server authority is already stale.
        await flushSaves();
        const runnerState = useStudioStore.getState();
        const runnerWorkspace = await currentSourceWorkspace();
        if (
          !runnerState.currentProject ||
          runnerState.currentProjectId !== initialState.currentProjectId ||
          hasPendingChanges() ||
          runnerState.saveConflict ||
          runnerState.saveError ||
          computeCogoProjectSha256(runnerState.currentProject) !==
            mutation.baseCogoProject.sha256 ||
          !cogoDesignMutationValuesEqual(runnerWorkspace, previousWorkspace)
        ) {
          throw new Error("DESIGN_MUTATION_RUNNER_PREFLIGHT_DIVERGED");
        }
        if (runnerState.projectSource === "server") {
          await getMatchingServerSourceProject(
            runnerState.currentProjectId,
            previousWorkspace,
          );
        }
        const patch = OpenPencilFigEditPatchSchema.parse(plan.sourcePatch);
        const result = await requestOpenPencilFigRevision({
          bundle: previousWorkspace.bundle,
          patch,
        });
        const revisionVisualDiff = await compareOpenPencilRevisionVisuals(
          previousWorkspace.bundle,
          result.bundle,
        );
        const sourceRevision = result.bundle.revisionLineage?.sourceRevision;
        const sourceSha256 = result.bundle.fileSha256;
        const sourceArtifactRef = result.bundle.sourceArtifactRef;
        if (
          typeof sourceRevision !== "number" ||
          !sourceSha256 ||
          !sourceArtifactRef
        ) {
          throw new Error(
            "Design mutation source revision returned incomplete immutable readback.",
          );
        }

        // The source runner and visual diff are asynchronous. Re-run every Cogo
        // and editor precondition before changing the active authority pointer.
        return withAutoSaveDispatchLock(async () => {
          const commitState = useStudioStore.getState();
          if (!commitState.currentProject || !commitState.currentProjectId) {
            throw new Error("Design mutation project closed before commit.");
          }
          if (
            hasPendingChanges() ||
            commitState.saveConflict ||
            commitState.saveError
          ) {
            throw new Error("DESIGN_MUTATION_PENDING_OR_FAILED_SAVE");
          }
          const commitWorkspace = await currentSourceWorkspace();
          if (
            !cogoDesignMutationValuesEqual(commitWorkspace, previousWorkspace)
          ) {
            throw new Error("DESIGN_MUTATION_SOURCE_WORKSPACE_DIVERGED");
          }
          plan = prepareOpenPencilCogoDesignMutation({
            mutation,
            workspace: commitWorkspace,
            currentProjectId: commitState.currentProjectId,
            currentProject: commitState.currentProject,
          });
          assertEditorDesignMutationTargets(
            commitState.currentProject,
            plan.elementSnapshots,
          );
          const invalidatedEvidence = {
            visualJudgements: Object.keys(commitWorkspace.visualJudgements)
              .length,
            revisionVisualDiff: Boolean(commitWorkspace.revisionVisualDiff),
            assemblyManifest: Boolean(commitWorkspace.assemblyManifest),
            candidateBinding: Boolean(commitWorkspace.candidateBinding),
            runtimeProof: Boolean(commitWorkspace.runtimeProof),
          };
          let workspace = recordSourceRevisionVisualDiff(
            carryOpenPencilSourceDecisionsToRevision(
              commitWorkspace,
              result.bundle,
              revisionVisualDiff.evaluatedAt,
            ),
            revisionVisualDiff,
          );
          const appliedAt = new Date().toISOString();
          const finalized = finalizeOpenPencilCogoDesignMutation(plan, {
            sourceId: result.bundle.sourceId,
            sourceRevision,
            sha256: sourceSha256,
            artifactRef: sourceArtifactRef,
            changedSurfaceIds: revisionVisualDiff.changedSurfaceIds,
            visualDiffStatus: revisionVisualDiff.status,
            appliedAt,
          });
          workspace = recordOpenPencilFigmaCompanionMutation(workspace, {
            mutation,
            readback: finalized.readback,
            updatedAt: appliedAt,
          });
          const inverse = designMutationRestoreInverse({
            projectId: commitState.currentProjectId,
            workspace: previousWorkspace,
            project: previousProject,
            expectedWorkspace: workspace,
            expectedProject: finalized.project,
            targets: plan.elementSnapshots.map(({ blockName, elementId }) => ({
              blockName,
              elementId,
            })),
          });

          let persistence: DesignMutationPersistenceResult | null = null;
          try {
            persistence = await persistCoordinatedDesignMutation({
              projectId: commitState.currentProjectId,
              nextProject: finalized.project,
              previousProject,
              nextWorkspace: workspace,
              previousWorkspace,
              blockName,
            });
            const postPersistState = useStudioStore.getState();
            if (
              hasPendingChanges() ||
              postPersistState.currentProjectId !==
                commitState.currentProjectId ||
              !postPersistState.currentProject ||
              computeCogoProjectSha256(postPersistState.currentProject) !==
                mutation.baseCogoProject.sha256 ||
              !postPersistState.openPencilSourceWorkspace ||
              !cogoDesignMutationValuesEqual(
                postPersistState.openPencilSourceWorkspace,
                previousWorkspace,
              )
            ) {
              throw new Error("DESIGN_MUTATION_COMMIT_SCOPE_DIVERGED");
            }
            assertEditorDesignMutationTargets(
              postPersistState.currentProject,
              plan.elementSnapshots,
            );
            commitCogoDesignMutationProject(
              finalized.project,
              finalized.elementSnapshots,
              commitState.projectSource === "server"
                ? commitState.isDirty
                : true,
            );
            withCoordinatedOpenPencilWorkspaceWrite(() =>
              useStudioStore.getState().setOpenPencilSourceWorkspace(workspace),
            );
          } catch (error) {
            if (persistence?.cogoBlockSave === "persisted") {
              try {
                await persistCoordinatedDesignMutation({
                  projectId: commitState.currentProjectId,
                  nextProject: previousProject,
                  previousProject: finalized.project,
                  nextWorkspace: previousWorkspace,
                  previousWorkspace: workspace,
                  blockName,
                });
              } catch (rollbackError) {
                throw new AggregateError(
                  [error, rollbackError],
                  "DESIGN_MUTATION_POST_PERSISTENCE_ROLLBACK_FAILED",
                );
              }
            } else if (persistence?.cogoBlockSave === "session-only") {
              transitionSessionOnlyDesignMutationAuthority({
                projectId: commitState.currentProjectId,
                previousWorkspaceHash: computeSourceWorkspaceHash(workspace),
                nextWorkspaceHash:
                  computeSourceWorkspaceHash(previousWorkspace),
              });
            }
            throw error;
          }
          if (!persistence) {
            throw new Error("DESIGN_MUTATION_PERSISTENCE_RESULT_MISSING");
          }
          activateOpenPencilSourceWorkspace();
          return {
            mutation: finalized.readback,
            source: summarizeOpenPencilFigmaSource(result.bundle),
            revisionLineage: result.bundle.revisionLineage,
            revisionVisualDiff,
            cogo: {
              projectId: commitState.currentProjectId,
              baseRevision: plan.baseCogoRevision,
              revision: plan.nextCogoRevision,
              sha256: finalized.projectSha256,
              targets: finalized.elementSnapshots.map(
                ({ blockName, elementId, pointer }) => ({
                  blockName,
                  elementId,
                  pointer,
                }),
              ),
              changedPaths: finalized.readback.changedPaths,
            },
            persistence,
            carriedForward: {
              pageDecisions: carriedDecisionCount(
                commitWorkspace.pageDecisions,
                workspace.pageDecisions,
              ),
              sectionDecisions: carriedDecisionCount(
                commitWorkspace.sectionDecisions,
                workspace.sectionDecisions,
              ),
              surfaceDecisions: carriedDecisionCount(
                commitWorkspace.surfaceDecisions,
                workspace.surfaceDecisions,
              ),
              tokenDecisions: carriedDecisionCount(
                commitWorkspace.tokenDecisions,
                workspace.tokenDecisions,
              ),
            },
            invalidatedEvidence,
            immutableRevisionArtifactDeletedOnUndo: false,
            _redoPolicy: "confirmation-required",
            _inverse: inverse,
          };
        });
      });
    },
  });

  mcpBus.registerTool("create_figma_companion_sync_back_envelope", {
    description:
      "Create and persist a bounded Figma Companion proposal from the approved mutation and immutable local revision already stored in the active OpenPencil workspace. The checkpoint must come from the Figma plugin.",
    metadata: {
      tags: ["write", "figma", "openpencil", "sync-back", "checkpoint"],
    },
    inputSchema: {
      type: "object",
      required: ["checkpoint"],
      properties: {
        checkpoint: objectSchema,
        patchId: stringSchema,
        idempotencyKey: stringSchema,
      },
    },
    handler: async (payload) => {
      const workspace = await currentSourceWorkspace();
      const pending = workspace.figmaCompanion.pendingMutation;
      if (!pending) {
        throw new Error("FIGMA_COMPANION_PERSISTED_MUTATION_REQUIRED");
      }
      const envelope = createFigmaCompanionSyncBackEnvelope({
        mutation: pending.mutation,
        finalizedReadback: pending.readback,
        checkpoint: parseFigmaCompanionCheckpoint(payload.checkpoint),
        patchId:
          typeof payload.patchId === "string"
            ? payload.patchId
            : `figma-patch-${crypto.randomUUID()}`,
        idempotencyKey:
          typeof payload.idempotencyKey === "string"
            ? payload.idempotencyKey
            : `figma-idempotency-${crypto.randomUUID()}`,
      });
      const next = recordOpenPencilFigmaCompanionPreparedPatch(
        workspace,
        envelope.patch,
        new Date().toISOString(),
      );
      await commitFigmaCompanionWorkspaceMetadata(workspace, next);
      return envelope;
    },
  });

  mcpBus.registerTool("record_figma_companion_sync_back_receipt", {
    description:
      "Verify a Figma Companion selected-node receipt against the persisted prepared patch and store scoped sync-back evidence. This is plugin self-attestation, not OAuth whole-file readback.",
    metadata: {
      tags: ["write", "figma", "openpencil", "sync-back", "receipt"],
    },
    inputSchema: {
      type: "object",
      required: ["receipt"],
      properties: { receipt: objectSchema },
    },
    handler: async (payload) => {
      const workspace = await currentSourceWorkspace();
      const patch = workspace.figmaCompanion.preparedPatch;
      if (!patch) {
        throw new Error("FIGMA_COMPANION_PREPARED_PATCH_REQUIRED");
      }
      assertFigmaCompanionReceipt(payload.receipt);
      const receipt = payload.receipt as FigmaCompanionReceipt;
      const evidence = deriveFigmaCompanionSyncBackEvidence({ patch, receipt });
      const next = recordOpenPencilFigmaCompanionSyncBackEvidence(
        workspace,
        evidence,
      );
      await commitFigmaCompanionWorkspaceMetadata(workspace, next);
      return {
        evidence,
        authorityBoundary:
          "plugin-self-attested-selected-node-receipt; OAuth whole-file readback pending",
      };
    },
  });

  mcpBus.registerTool("inspect_openpencil_source_workspace", {
    description:
      "Inspect page, story, surface, and token decisions in the persistent OpenPencil source workspace.",
    metadata: {
      readOnly: true,
      tags: ["figma", "openpencil", "source-workspace", "mapping"],
    },
    inputSchema: { type: "object", properties: {} },
    handler: async () =>
      summarizeOpenPencilSourceWorkspace(await currentSourceWorkspace()),
  });

  mcpBus.registerTool("set_openpencil_page_decision", {
    description:
      "Accept, reject, or defer a Figma page and optionally map it to a story.",
    metadata: { tags: ["figma", "openpencil", "source-workspace", "page"] },
    inputSchema: {
      type: "object",
      required: ["pageId", "status"],
      properties: {
        pageId: stringSchema,
        status: {
          type: "string",
          enum: ["pending", "accepted", "rejected"],
        },
        storyId: stringSchema,
        storyTitle: stringSchema,
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = setSourcePageDecision(previous, {
        pageId: String(payload.pageId),
        status: payload.status as "pending" | "accepted" | "rejected",
        ...(payload.storyId ? { storyId: String(payload.storyId) } : {}),
        ...(payload.storyTitle
          ? { storyTitle: String(payload.storyTitle) }
          : {}),
        ...(payload.reason ? { reason: String(payload.reason) } : {}),
        actor: payload.actor === "human" ? "human" : "agent",
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        decision: workspace.pageDecisions[String(payload.pageId)],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("set_openpencil_section_decision", {
    description:
      "Accept, reject, or defer a Figma section and map it to an application story.",
    metadata: {
      tags: ["figma", "openpencil", "source-workspace", "story"],
    },
    inputSchema: {
      type: "object",
      required: ["sectionId", "status"],
      properties: {
        sectionId: stringSchema,
        status: {
          type: "string",
          enum: ["pending", "accepted", "rejected"],
        },
        storyId: stringSchema,
        storyTitle: stringSchema,
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = setSourceSectionDecision(previous, {
        sectionId: String(payload.sectionId),
        status: payload.status as "pending" | "accepted" | "rejected",
        ...(payload.storyId ? { storyId: String(payload.storyId) } : {}),
        ...(payload.storyTitle
          ? { storyTitle: String(payload.storyTitle) }
          : {}),
        ...(payload.reason ? { reason: String(payload.reason) } : {}),
        actor: payload.actor === "human" ? "human" : "agent",
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        decision: workspace.sectionDecisions[String(payload.sectionId)],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("set_openpencil_surface_mapping", {
    description:
      "Accept, reject, or defer a Figma surface and assign its page/story role.",
    metadata: {
      tags: ["figma", "openpencil", "source-workspace", "surface"],
    },
    inputSchema: {
      type: "object",
      required: ["surfaceId", "status", "role"],
      properties: {
        surfaceId: stringSchema,
        status: {
          type: "string",
          enum: ["pending", "accepted", "rejected"],
        },
        role: {
          type: "string",
          enum: ["screen", "reference", "annotation", "cover", "history"],
        },
        targetPageId: stringSchema,
        storyId: stringSchema,
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = setSourceSurfaceDecision(previous, {
        surfaceId: String(payload.surfaceId),
        status: payload.status as "pending" | "accepted" | "rejected",
        role: payload.role as
          | "screen"
          | "reference"
          | "annotation"
          | "cover"
          | "history",
        ...(payload.targetPageId
          ? { targetPageId: String(payload.targetPageId) }
          : {}),
        ...(payload.storyId ? { storyId: String(payload.storyId) } : {}),
        ...(payload.reason ? { reason: String(payload.reason) } : {}),
        actor: payload.actor === "human" ? "human" : "agent",
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        decision: workspace.surfaceDecisions[String(payload.surfaceId)],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("set_openpencil_token_decision", {
    description:
      "Accept, reject, or defer a Figma design-token candidate and optionally name its canonical token.",
    metadata: {
      tags: ["figma", "openpencil", "source-workspace", "design-token"],
    },
    inputSchema: {
      type: "object",
      required: ["tokenId", "status"],
      properties: {
        tokenId: stringSchema,
        status: {
          type: "string",
          enum: ["pending", "accepted", "rejected"],
        },
        canonicalToken: stringSchema,
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = setSourceTokenDecision(previous, {
        tokenId: String(payload.tokenId),
        status: payload.status as "pending" | "accepted" | "rejected",
        ...(payload.canonicalToken
          ? { canonicalToken: String(payload.canonicalToken) }
          : {}),
        ...(payload.reason ? { reason: String(payload.reason) } : {}),
        actor: payload.actor === "human" ? "human" : "agent",
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        decision: workspace.tokenDecisions[String(payload.tokenId)],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("apply_openpencil_suggested_mapping", {
    description:
      "Apply geometry-derived page, section, and surface suggestions; mobile surfaces become screens and non-mobile surfaces remain excluded references.",
    metadata: {
      tags: ["figma", "openpencil", "source-workspace", "mapping", "bulk"],
    },
    inputSchema: {
      type: "object",
      properties: {
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = applySuggestedSourceMapping(previous, {
        actor: payload.actor === "human" ? "human" : "agent",
        reason:
          typeof payload.reason === "string"
            ? payload.reason
            : "Applied geometry-derived mapping suggestions.",
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        ...summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("set_all_openpencil_token_decisions", {
    description:
      "Accept or reject all current OpenPencil design-token candidates as one revision.",
    metadata: {
      tags: ["figma", "openpencil", "source-workspace", "design-token", "bulk"],
    },
    inputSchema: {
      type: "object",
      required: ["status"],
      properties: {
        status: { type: "string", enum: ["accepted", "rejected"] },
        reason: stringSchema,
        actor: { type: "string", enum: ["human", "agent"] },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = setAllSourceTokenDecisions(previous, {
        status: payload.status as "accepted" | "rejected",
        actor: payload.actor === "human" ? "human" : "agent",
        ...(payload.reason ? { reason: String(payload.reason) } : {}),
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        ...summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("request_openpencil_visual_judgement", {
    description:
      "Queue the production bounded Gemma vision judge for accepted Figma screens, wait for its R2-backed evidence, and persist the verified judgement. This tool cannot write application JSON.",
    metadata: {
      tags: ["figma", "openpencil", "visual-judge", "gemma", "evidence"],
    },
    inputSchema: {
      type: "object",
      properties: {
        surfaceIds: { type: "array", items: stringSchema },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const surfaceIds = Array.isArray(payload.surfaceIds)
        ? payload.surfaceIds.map(String)
        : Object.values(previous.surfaceDecisions)
            .filter(
              (decision) =>
                decision.status === "accepted" && decision.role === "screen",
            )
            .map((decision) => decision.surfaceId);
      const evidence = await requestOpenPencilVisualJudgement({
        bundle: previous.bundle,
        surfaceIds,
      });
      const workspace = recordSourceVisualJudgement(previous, {
        judgementId: evidence.judgementId,
        judge: "gemma",
        model: evidence.model,
        status: evidence.status,
        confidence: evidence.confidence,
        surfaceIds: evidence.surfaceIds,
        annotations: evidence.annotations,
        evidenceRefs: evidence.evidenceRefs,
        reason: evidence.reason,
        updatedAt: evidence.updatedAt,
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        judgement: workspace.visualJudgements[evidence.judgementId],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("record_openpencil_visual_judgement", {
    description:
      "Record an explicit human visual-semantic decision for accepted Figma screen previews. Gemma evidence must use request_openpencil_visual_judgement.",
    metadata: {
      tags: ["figma", "openpencil", "visual-judge", "human", "evidence"],
    },
    inputSchema: {
      type: "object",
      required: ["judge", "status", "confidence", "surfaceIds", "evidenceRefs"],
      properties: {
        judgementId: stringSchema,
        judge: { type: "string", enum: ["human"] },
        model: stringSchema,
        status: {
          type: "string",
          enum: ["approved", "rejected", "blocked"],
        },
        confidence: { type: "number", minimum: 0, maximum: 1 },
        surfaceIds: { type: "array", items: stringSchema },
        annotations: {
          type: "array",
          items: {
            type: "object",
            properties: {
              surfaceId: stringSchema,
              kind: {
                type: "string",
                enum: [
                  "component",
                  "icon",
                  "text",
                  "layout",
                  "spacing",
                  "token",
                  "missing-surface",
                  "mismatch",
                ],
              },
              severity: {
                type: "string",
                enum: ["info", "warning", "blocking"],
              },
              message: stringSchema,
              sourceLocator: stringSchema,
            },
            required: ["kind", "severity", "message"],
          },
        },
        evidenceRefs: { type: "array", items: stringSchema },
        reason: stringSchema,
      },
    },
    handler: async (payload) => {
      if (payload.judge !== "human") {
        throw new Error(
          "Gemma evidence must be created by request_openpencil_visual_judgement.",
        );
      }
      const previous = await currentSourceWorkspace();
      const workspace = recordSourceVisualJudgement(previous, {
        judgementId:
          typeof payload.judgementId === "string"
            ? payload.judgementId
            : undefined,
        judge: "human",
        status: payload.status as "approved" | "rejected" | "blocked",
        confidence: Number(payload.confidence),
        surfaceIds: Array.isArray(payload.surfaceIds)
          ? payload.surfaceIds.map(String)
          : [],
        annotations: Array.isArray(payload.annotations)
          ? (payload.annotations as Parameters<
              typeof recordSourceVisualJudgement
            >[1]["annotations"])
          : [],
        evidenceRefs: Array.isArray(payload.evidenceRefs)
          ? payload.evidenceRefs.map(String)
          : [],
        reason: typeof payload.reason === "string" ? payload.reason : undefined,
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        judgement:
          workspace.visualJudgements[
            typeof payload.judgementId === "string"
              ? payload.judgementId
              : (Object.keys(workspace.visualJudgements).at(-1) ?? "")
          ],
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("build_openpencil_source_assembly", {
    description:
      "Build a deterministic selected-source assembly sidecar after mapping, token, and visual gates pass. It is not Coogo JSON.",
    metadata: {
      tags: ["figma", "openpencil", "assembly", "evidence"],
    },
    inputSchema: {
      type: "object",
      properties: {
        manifestId: stringSchema,
        evidenceRefs: { type: "array", items: stringSchema },
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const workspace = buildOpenPencilSourceAssemblyManifest(previous, {
        manifestId:
          typeof payload.manifestId === "string"
            ? payload.manifestId
            : undefined,
        evidenceRefs: Array.isArray(payload.evidenceRefs)
          ? payload.evidenceRefs.map(String)
          : [],
      });
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        manifest: workspace.assemblyManifest,
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("request_openpencil_runtime_proof", {
    description:
      "Queue the bounded Playwright runtime proof service for a generated source assembly and persist its server-verified desktop, mobile, console, interaction, and canary evidence.",
    metadata: {
      tags: ["figma", "openpencil", "runtime-proof", "playwright", "evidence"],
    },
    inputSchema: {
      type: "object",
      properties: {
        previewUrl: stringSchema,
        applicationArtifactRef: stringSchema,
      },
    },
    handler: async (payload) => {
      const previous = await currentSourceWorkspace();
      const manifest = previous.assemblyManifest;
      if (!manifest || manifest.status !== "validated") {
        throw new Error("Runtime proof requires a validated source assembly.");
      }
      if (previous.bundle.generationAdmission.status !== "admitted") {
        throw new Error(
          "Runtime proof requires Coogo JSON generation admission.",
        );
      }
      const candidate = previous.candidateBinding;
      if (!candidate) {
        throw new Error("Runtime proof requires a bound source candidate.");
      }
      const requestedPreviewUrl =
        typeof payload.previewUrl === "string"
          ? canonicalCandidatePreviewUrl(payload.previewUrl)
          : candidate.previewUrl;
      const requestedArtifactRef =
        typeof payload.applicationArtifactRef === "string"
          ? payload.applicationArtifactRef
          : candidate.applicationArtifactRef;
      if (
        requestedPreviewUrl !== candidate.previewUrl ||
        requestedArtifactRef !== candidate.applicationArtifactRef
      ) {
        throw new Error(
          "Runtime proof request does not match the bound source candidate.",
        );
      }
      const proof = await requestOpenPencilRuntimeProof({
        bundle: previous.bundle,
        manifestId: manifest.manifestId,
        previewUrl: candidate.previewUrl,
        applicationArtifactRef: candidate.applicationArtifactRef,
      });
      const workspace = recordVerifiedOpenPencilSourceRuntimeProof(
        previous,
        proof,
      );
      useStudioStore.getState().setOpenPencilSourceWorkspace(workspace);
      await saveOpenPencilSourceWorkspaceToCurrentProject(workspace);
      return {
        proof: workspace.runtimeProof,
        workspace: summarizeOpenPencilSourceWorkspace(workspace),
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("clear_openpencil_figma_source", {
    description:
      "cogo.xyz Studio: clear the current OpenPencil .fig source-fact bundle from the Studio session.",
    metadata: { tags: ["cogo-xyz", "figma", "openpencil", "source-facts"] },
    inputSchema: { type: "object", properties: {} },
    handler: async () => {
      const previous = useStudioStore.getState().openPencilSourceWorkspace;
      if (currentProjectSourceAuthority()) {
        throw new Error(
          "The active source-derived candidate must retain its source authority. Open another project before clearing the source workspace.",
        );
      }
      await clearOpenPencilSourceWorkspaceFromCurrentProject();
      return {
        status: "cleared",
        cogoJsonGeneration: "disabled",
        _inverse: sourceWorkspaceInverse(previous),
      };
    },
  });

  mcpBus.registerTool("import_figma_frame", {
    description:
      "cogo.xyz Phase 1: create a read-only Figma frame WorkItem with mapping evidence and optional preview record.",
    metadata: { tags: ["cogo-xyz", "phase-1", "figma"] },
    inputSchema: {
      type: "object",
      required: [
        "workspaceId",
        "figmaFileId",
        "figmaNodeId",
        "createPreview",
        "templateSelection",
      ],
      properties: {
        workspaceId: stringSchema,
        figmaFileId: stringSchema,
        figmaNodeId: stringSchema,
        createPreview: booleanSchema,
        createdBy: stringSchema,
        figmaAccessToken: stringSchema,
        figmaTokenRef: stringSchema,
        figmaUrl: stringSchema,
        audience: stringSchema,
        tags: { type: "array", items: stringSchema },
        templateSelection: {
          type: "object",
          required: ["userIntent", "scope"],
          properties: {
            userIntent: stringSchema,
            scope: {
              type: "string",
              enum: ["project", "template", "designSystem", "globalLibrary"],
            },
            expectedViewportClass: {
              type: "string",
              enum: ["mobile", "tablet", "desktop", "unknown"],
            },
            expectedRegions: { type: "array", items: stringSchema },
            expectedComponentKinds: { type: "array", items: stringSchema },
            expectedInteractionAffordances: {
              type: "array",
              items: stringSchema,
            },
          },
        },
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("import_figma_frame");
      const currentProjectId = useStudioStore.getState().currentProjectId;
      if (!currentProjectId) {
        // A caller with no open project is not a fault, it is a REFUSAL — and
        // the dashboard offers this path to exactly that caller. So it is
        // written the way every other project-required refusal in Studio is
        // written (`ONTOLOGY_READBACK_PROJECT_REQUIRED: …`,
        // `SOURCE_ASSESSMENT_READBACK_PROJECT_REQUIRED: …`,
        // `CANDIDATE_VERIFICATION_PROJECT_REQUIRED: …`): a named code the
        // reader can quote, then one sentence naming the precondition and what
        // to do about it. What it replaced named the tool's internal id and an
        // implementation reason ("tenant-scoped ontology persistence") — true,
        // and useless to the person holding the screen.
        //
        // It stays on the FAILURE channel deliberately, and that is measured,
        // not assumed. `studio-mcp-bus`'s `innerHandler` keeps only
        // `Error.message` (`{ success: false, error: message }`) and turns any
        // RETURNED value into `{ success: true, data }`. Both consumers branch
        // on `result.success`: `studio-dashboard/pages/import-page.tsx`
        // announces `sourcesCopy.after.steps.sourceSaved` and routes to the
        // library on success, and renders `result.error` verbatim via
        // `setMessage` on failure; `features/chat/components/studio-composer.tsx`
        // summarises `result.data` on success. A structured refusal returned as
        // a value would therefore reach the reader as "source saved" plus a
        // navigation away from the page — a refusal rendered as a success. The
        // string IS the structure the consumers can read, so the reason lives
        // in it.
        throw new Error(
          "IMPORT_FIGMA_FRAME_PROJECT_REQUIRED: Open a Studio project before importing a Figma frame.",
        );
      }
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzImportFigmaFrameInput & {
        figmaAccessToken?: string;
        figmaTokenRef?: string;
        templateSelection: NonNullable<
          FigmaFrameImportInput["templateSelection"]
        >;
      };
      const approval =
        import.meta.env.MODE === "test"
          ? {
              actorId: "studio-test-user",
              confirmationId: "studio-test-confirmation",
              approvedAt: "2026-07-18T00:00:00.000Z",
            }
          : consumeStudioCapabilityApprovalGrant({
              token: payload._capabilityApprovalToken,
              tool: "cogo.studio.import_figma_frame",
              params: payload,
              projectId: currentProjectId,
              failureScope: "studio",
            });
      let figmaTokenRef = input.figmaTokenRef?.trim();
      if (!figmaTokenRef) {
        const storedToken = await storeFigmaAccessTokenOnServer(
          input.figmaAccessToken ?? "",
          { workspaceId: input.workspaceId },
        );
        figmaTokenRef = storedToken.tokenRef?.trim();
        if (!storedToken.ok || !figmaTokenRef) {
          throw new Error(
            `Figma token ${storedToken.control.status}: ${storedToken.message}`,
          );
        }
      }
      const imported = await importFigmaFrameAsCogoApp({
        projectId: currentProjectId,
        figmaFileId: input.figmaFileId,
        figmaNodeId: input.figmaNodeId,
        figmaTokenRef,
        workspaceId: input.workspaceId,
        figmaUrl: input.figmaUrl,
        audience: input.audience,
        tags: input.tags,
        templateSelection: input.templateSelection,
        selectionConfirmation: {
          schemaVersion: "cogo.human-template-selection-confirmation.v1",
          actorId: approval.actorId,
          confirmationId: approval.confirmationId,
          approvedAt: approval.approvedAt,
          tool: "cogo.studio.import_figma_frame",
          projectId: currentProjectId,
        },
      });
      const sourceOntologyStorage = imported.sourceOntologyStorage;
      const sourceOntologyStorageAccepted =
        (sourceOntologyStorage?.status === "readback-hit" &&
          sourceOntologyStorage.source ===
            "cloudflare-d1-r2-derived-readback") ||
        (sourceOntologyStorage?.status === "skipped" &&
          import.meta.env.MODE === "test");
      if (!sourceOntologyStorageAccepted) {
        throw new Error(
          sourceOntologyStorage?.error ??
            "Figma ontology persistence failed closed",
        );
      }
      const result = cogoXyzRegistry.importFigmaFrame(
        {
          workspaceId: input.workspaceId,
          figmaFileId: input.figmaFileId,
          figmaNodeId: input.figmaNodeId,
          createPreview: input.createPreview,
          createdBy: input.createdBy,
          figmaUrl: input.figmaUrl,
          audience: input.audience,
          tags: input.tags,
        },
        {
          bundleRef: imported.bundleRef,
          previewUrl: imported.previewUrl,
          frameName: imported.frameName,
          unsupportedNodes: imported.mapping.unsupportedNodes,
          matchedNodes: imported.mapping.matchedNodes,
          nodeCount: imported.mapping.nodeCount,
          textNodeCount: imported.mapping.textNodeCount,
          extractedTokens: imported.mapping.extractedTokens,
          matchedComponents: imported.mapping.matchedComponents,
          componentMatchRate: imported.mapping.componentMatchRate,
          unsupportedNodeCount: imported.mapping.unsupportedNodeCount,
          ontologyValidation: imported.mapping.ontologyValidation,
          referenceParity: imported.mapping.referenceParity,
          designTemplateApplication: imported.mapping.designTemplateApplication,
          sourceOntologyStorage: imported.sourceOntologyStorage,
          sourceFrame: imported.sourceFrame,
          sourceFrames: imported.sourceFrames,
          sourceFrameSelection: imported.mapping.sourceFrameSelection,
          pageCount: imported.pageCount,
          actionFlowCount: imported.actionFlowCount,
          metadata: {
            importedProjectId: imported.projectId,
            importedProjectName: imported.name,
            textNodeCount: imported.mapping.textNodeCount,
            nodeCount: imported.mapping.nodeCount,
            ontologyValidation: imported.mapping.ontologyValidation,
            referenceParity: imported.mapping.referenceParity,
            designTemplateApplication:
              imported.mapping.designTemplateApplication,
            sourceOntologyStorage: imported.sourceOntologyStorage,
            sourceFrame: imported.sourceFrame,
            sourceFrames: imported.sourceFrames,
            sourceFrameSelection: imported.mapping.sourceFrameSelection,
            pageCount: imported.pageCount,
            actionFlowCount: imported.actionFlowCount,
          },
        },
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("attach_github_pr_preview", {
    description:
      "cogo.xyz Phase 1: draft a GitHub PR preview attachment evidence record without external write access.",
    metadata: { tags: ["cogo-xyz", "phase-1", "github"] },
    inputSchema: {
      type: "object",
      required: [
        "workspaceId",
        "repo",
        "prNumber",
        "previewUrl",
        "evidenceIds",
        "commentMode",
      ],
      properties: {
        workspaceId: stringSchema,
        repo: stringSchema,
        prNumber: { type: "integer" },
        previewUrl: stringSchema,
        evidenceIds: { type: "array", items: stringSchema },
        commentMode: { type: "string", enum: ["draft", "post"] },
        commitSha: stringSchema,
        branch: stringSchema,
        baseBranch: stringSchema,
        changedFiles: { type: "array", items: stringSchema },
        uiRelatedFiles: { type: "array", items: stringSchema },
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const result = cogoXyzRegistry.attachGithubPrPreview(
        payload as unknown as CogoXyzAttachGithubPrPreviewInput,
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("publish_github_pr_preview", {
    description:
      "cogo.xyz Phase 3: record or execute an approved GitHub PR preview comment/check write boundary.",
    metadata: { tags: ["cogo-xyz", "phase-3", "github", "approval"] },
    inputSchema: {
      type: "object",
      required: ["workItemId", "repo", "prNumber", "action", "mode"],
      properties: {
        workItemId: stringSchema,
        repo: stringSchema,
        prNumber: { type: "integer" },
        action: {
          type: "string",
          enum: ["comment", "check", "comment_and_check"],
        },
        mode: {
          type: "string",
          enum: ["dry_run", "record_approval", "execute"],
        },
        approved: booleanSchema,
        previewUrl: stringSchema,
        commentBody: stringSchema,
        commitSha: stringSchema,
        branch: stringSchema,
        createdBy: stringSchema,
        githubCommentUrl: stringSchema,
        githubCheckUrl: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceReleaseAdmitted();
      await loadCurrentCogoXyzRegistry();
      const result = cogoXyzRegistry.publishGithubPrPreview(
        payload as unknown as CogoXyzPublishGithubPrPreviewInput,
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("share_cogo_preview", {
    description:
      "cogo.xyz Phase 4: create a shareable preview link and record share evidence without external writes.",
    metadata: { tags: ["cogo-xyz", "phase-4", "share"] },
    inputSchema: {
      type: "object",
      required: ["previewUrl", "visibility"],
      properties: {
        previewUrl: stringSchema,
        visibility: { type: "string", enum: ["public", "unlisted"] },
        createdBy: stringSchema,
        registryProjectId: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceReleaseAdmitted();
      const registryScope = await loadRequestedCogoXyzRegistry(payload);
      const result = cogoXyzRegistry.shareCogoPreview(
        payload as unknown as CogoXyzShareCogoPreviewInput,
      );
      await saveRequestedCogoXyzRegistry(registryScope);
      return result;
    },
  });

  mcpBus.registerTool("export_cogo_bundle", {
    description:
      "cogo.xyz Phase 1: export a recorded Coogo bundle as a traceable artifact reference.",
    metadata: { tags: ["cogo-xyz", "phase-1", "export"] },
    inputSchema: {
      type: "object",
      required: ["bundleId", "format"],
      properties: {
        bundleId: stringSchema,
        format: {
          type: "string",
          enum: ["cogo_json", "react", "html", "figma", "deploy_package"],
        },
      },
    },
    handler: async (payload) => {
      if (payload.format === "cogo_json") {
        await assertCurrentSourceGenerationAdmitted();
      } else {
        await assertCurrentSourceReleaseAdmitted();
      }
      await loadCurrentCogoXyzRegistry();
      const result = cogoXyzRegistry.exportCogoBundle(
        payload as unknown as CogoXyzExportCogoBundleInput,
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("prepare_cogo_deploy", {
    description:
      "cogo.xyz Phase 4: prepare a deployment package/evidence record while keeping production deploy approval explicit.",
    metadata: { tags: ["cogo-xyz", "phase-4", "deploy"] },
    inputSchema: {
      type: "object",
      required: ["bundleId", "provider", "target"],
      properties: {
        bundleId: stringSchema,
        provider: stringSchema,
        target: { type: "string", enum: ["preview", "production"] },
        productionUrl: stringSchema,
        sourceSha: stringSchema,
        deploymentId: stringSchema,
        approved: booleanSchema,
        registryProjectId: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceReleaseAdmitted();
      const registryScope = await loadRequestedCogoXyzRegistry(payload);
      const result = cogoXyzRegistry.prepareCogoDeploy(
        payload as unknown as CogoXyzPrepareDeployInput,
      );
      await saveRequestedCogoXyzRegistry(registryScope);
      return result;
    },
  });

  mcpBus.registerTool("record_cloudflare_deployment", {
    description:
      "Library Hub: record a Cloudflare Pages/Workers deployment and bind it to a Git source ref.",
    metadata: { tags: ["library-hub", "cloudflare", "deploy", "git"] },
    inputSchema: {
      type: "object",
      required: [
        "surface",
        "projectName",
        "environment",
        "deploymentId",
        "status",
        "gitSource",
      ],
      properties: {
        workItemId: stringSchema,
        bundleId: stringSchema,
        surface: { type: "string", enum: ["pages", "workers"] },
        accountId: stringSchema,
        projectName: stringSchema,
        environment: { type: "string", enum: ["preview", "production"] },
        deploymentId: stringSchema,
        versionId: stringSchema,
        deploymentUrl: stringSchema,
        previewUrl: stringSchema,
        productionUrl: stringSchema,
        status: stringSchema,
        gitSource: {
          type: "object",
          required: ["provider", "commitSha"],
          properties: {
            provider: stringSchema,
            repo: stringSchema,
            owner: stringSchema,
            branch: stringSchema,
            commitSha: stringSchema,
            prNumber: { type: "integer" },
            workflowRunId: stringSchema,
            checkRunId: stringSchema,
            sourceUrl: stringSchema,
          },
        },
        deployedAt: stringSchema,
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const result = cogoXyzRegistry.recordCloudflareDeployment(
        payload as unknown as CogoXyzRecordCloudflareDeploymentInput,
      );
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("list_cloudflare_deployments", {
    description:
      "Library Hub: list recorded Cloudflare deployments by WorkItem, project, environment, or commit SHA.",
    metadata: {
      readOnly: true,
      tags: ["library-hub", "cloudflare", "deploy", "git"],
    },
    inputSchema: {
      type: "object",
      properties: {
        workItemId: stringSchema,
        projectName: stringSchema,
        environment: stringSchema,
        commitSha: stringSchema,
        limit: { type: "integer" },
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      return cogoXyzRegistry.listCloudflareDeployments(
        payload as unknown as CogoXyzListCloudflareDeploymentsInput,
      );
    },
  });

  mcpBus.registerTool("list_cogo_templates", {
    description:
      "cogo.xyz Phase 5: list gallery-ready Coogo templates for remix workflows.",
    metadata: {
      readOnly: true,
      tags: ["cogo-xyz", "phase-5", "templates", "gallery"],
    },
    inputSchema: {
      type: "object",
      properties: {
        category: stringSchema,
        query: stringSchema,
        limit: { type: "integer" },
      },
    },
    handler: async (payload) =>
      listCogoXyzTemplates(payload as unknown as CogoXyzListTemplatesInput),
  });

  mcpBus.registerTool("remix_cogo_template", {
    description:
      "cogo.xyz Phase 5: create a Coogo WorkItem/preview from an existing Studio template.",
    metadata: { tags: ["cogo-xyz", "phase-5", "templates", "remix"] },
    inputSchema: {
      type: "object",
      required: ["workspaceId", "templateId"],
      properties: {
        workspaceId: stringSchema,
        templateId: stringSchema,
        name: stringSchema,
        createdBy: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceGenerationAdmitted();
      assertGenericOperationHasNoActiveSource("remix_cogo_template");
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzRemixTemplateInput;
      const remixed = await remixCogoXyzTemplate(input);
      const result = cogoXyzRegistry.createCogoApp(
        {
          workspaceId: input.workspaceId,
          title: remixed.projectName,
          sourceType: "template_remix",
          inputType: "markdown",
          input: [
            `# ${remixed.template.label}`,
            remixed.template.description,
            `Template ID: ${remixed.template.id}`,
            `Category: ${remixed.template.category}`,
          ].join("\n\n"),
          target: "app",
          createdBy: input.createdBy,
        },
        {
          bundleRef: remixed.bundleRef,
          previewUrl: remixed.previewUrl,
          toolName: "remix_cogo_template",
          metadata: {
            importSource: "template_remix",
            templateId: remixed.template.id,
            templateLabel: remixed.template.label,
            templateCategory: remixed.template.category,
            templateRoute: remixed.template.route,
            templateKeywords: remixed.template.keywords,
            templateBlocks: remixed.template.blocks,
            importedProjectId: remixed.projectId,
            galleryCandidateUrl: remixed.galleryCandidateUrl,
          },
        },
      );
      await saveCurrentCogoXyzRegistry();
      return {
        workItemId: result.workItemId,
        bundleId: result.bundleId,
        previewUrl: result.previewUrl,
        evidenceId: result.evidenceId,
        status: result.status,
        templateId: remixed.template.id,
        projectId: remixed.projectId,
        galleryCandidateUrl: remixed.galleryCandidateUrl,
      };
    },
  });

  mcpBus.registerTool("publish_cogo_gallery_item", {
    description:
      "cogo.xyz Phase 5: record a public gallery launch kit for a Coogo WorkItem with share, remix, and made-with-Coogo attribution.",
    metadata: { tags: ["cogo-xyz", "phase-5", "gallery"] },
    inputSchema: {
      type: "object",
      required: ["workItemId", "visibility"],
      properties: {
        workItemId: stringSchema,
        visibility: { type: "string", enum: ["public", "unlisted"] },
        title: stringSchema,
        audience: stringSchema,
        tags: { type: "array", items: stringSchema },
        remixSourceTemplateId: stringSchema,
        createdBy: stringSchema,
        registryProjectId: stringSchema,
      },
    },
    handler: async (payload) => {
      await assertCurrentSourceReleaseAdmitted();
      const registryScope = await loadRequestedCogoXyzRegistry(payload);
      const result = cogoXyzRegistry.publishGalleryItem(
        payload as unknown as CogoXyzPublishGalleryItemInput,
      );
      await saveRequestedCogoXyzRegistry(registryScope);
      return result;
    },
  });

  mcpBus.registerTool("get_cogo_xyz_evidence_packet", {
    description:
      "cogo.xyz Phase 1: read the WorkItem, bundle, preview, evidence, and ToolRun packet for a WorkItem.",
    metadata: { readOnly: true, tags: ["cogo-xyz", "phase-1", "evidence"] },
    inputSchema: {
      type: "object",
      required: ["workItemId"],
      properties: {
        workItemId: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const packet = cogoXyzRegistry.getEvidencePacket(
        payload.workItemId as string,
      );
      if (!packet)
        throw new Error(`Unknown cogo.xyz WorkItem: ${payload.workItemId}`);
      return packet;
    },
  });

  mcpBus.registerTool("list_library_items", {
    description:
      "Library Hub: list generated Coogo assets with source, proof, archive, and search filters.",
    metadata: { readOnly: true, tags: ["library-hub", "cogo-xyz", "query"] },
    inputSchema: {
      type: "object",
      properties: {
        workspaceId: stringSchema,
        query: stringSchema,
        sourceType: stringSchema,
        proofLevel: stringSchema,
        includeArchived: booleanSchema,
        limit: { type: "integer" },
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      return cogoXyzRegistry.listLibraryItems(
        payload as unknown as CogoXyzListLibraryItemsInput,
      );
    },
  });

  mcpBus.registerTool("get_library_item", {
    description:
      "Library Hub: read one generated Coogo asset with packet and governance detail.",
    metadata: { readOnly: true, tags: ["library-hub", "cogo-xyz", "query"] },
    inputSchema: {
      type: "object",
      required: ["workItemId"],
      properties: {
        workItemId: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzGetLibraryItemInput;
      const item = cogoXyzRegistry.getLibraryItem(input.workItemId);
      if (!item)
        throw new Error(`Unknown Library Hub WorkItem: ${input.workItemId}`);
      return item;
    },
  });

  mcpBus.registerTool("archive_library_item", {
    description:
      "Library Hub: archive or restore a generated Coogo asset without deleting evidence.",
    metadata: { tags: ["library-hub", "cogo-xyz", "archive"] },
    inputSchema: {
      type: "object",
      required: ["workItemId", "archived"],
      properties: {
        workItemId: stringSchema,
        archived: booleanSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const input = payload as unknown as CogoXyzArchiveLibraryItemInput;
      const result = cogoXyzRegistry.archiveLibraryItem(
        input.workItemId,
        input.archived,
      );
      if (!result)
        throw new Error(`Unknown Library Hub WorkItem: ${input.workItemId}`);
      await saveCurrentCogoXyzRegistry();
      return result;
    },
  });

  mcpBus.registerTool("get_cogo_xyz_tool_run", {
    description: "cogo.xyz Phase 2: read a recorded ToolRun by id.",
    metadata: { readOnly: true, tags: ["cogo-xyz", "phase-2", "tool-run"] },
    inputSchema: {
      type: "object",
      required: ["toolRunId"],
      properties: {
        toolRunId: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const toolRun = cogoXyzRegistry.getToolRun(payload.toolRunId as string);
      if (!toolRun)
        throw new Error(`Unknown cogo.xyz ToolRun: ${payload.toolRunId}`);
      return toolRun;
    },
  });

  mcpBus.registerTool("get_cogo_xyz_governance_report", {
    description:
      "cogo.xyz Phase 7: read enterprise governance/audit status for a WorkItem.",
    metadata: {
      readOnly: true,
      tags: ["cogo-xyz", "phase-7", "governance", "audit"],
    },
    inputSchema: {
      type: "object",
      required: ["workItemId"],
      properties: {
        workItemId: stringSchema,
      },
    },
    handler: async (payload) => {
      await loadCurrentCogoXyzRegistry();
      const report = cogoXyzRegistry.getGovernanceReport(
        payload.workItemId as string,
      );
      if (!report)
        throw new Error(`Unknown cogo.xyz WorkItem: ${payload.workItemId}`);
      return report;
    },
  });
}
